import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import { uniqueId } from 'lodash';
import * as _ from 'lodash';
import ReactResizeDetector from 'react-resize-detector';
import injectReducer from 'utils/injectReducer';
import { makeSelectTripInterruptionTugo, makeSelectTripInterruptionTugoErrors } from './selectors';
import reducer from './reducer';
import messages from './messages';
import PageSectionHeader from '../components/PageSectionHeader';
import { ValidateGroup } from '../../../components/Validation';
import { Validate, ErrorMessage } from '../../../components/Validation/';
import { required } from '../../../utils/validation/validator';
import { handleFormDataChange, nextSlide, resetFormFields } from './actions';
import { saveFormData } from '../actions';
import { makeSelectCurrentStep } from '../../Wizard/selectors';
import { makeSelectProgressBarStatus } from '../../ProgressBar/selectors';
import { makeSelectInsuredsList, makeSelectIsClaimForFieldEnabled, makeSelectFormHasChanges } from '../../App/selectors';
import { setFormHasChanges } from '../../App/actions';
import WizardNavigationButtons from '../components/WizardNavigationButtons';
import FNOLTooltip from '../../../components/FNOLTooltip';
import { HealthExpenseList } from './components/HealthExpense/index';

export class TripInterruptionTugo extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      validationCounter: 0,
      errors: [],
    };
    this.validatorGroup = new ValidateGroup();
    this.handleNext = this.handleNext.bind(this);
    this.checkFormError = this.checkFormError.bind(this);
    this.onResize = this.onResize.bind(this);
    this.saveButtonId = uniqueId();
    this.clickedItemId = '';
    this.nextButton = null;
  }

  componentWillReceiveProps(nextProps) {
    const { currentStep, step, stepName } = this.props;
    if ((stepName && nextProps.validateStep === stepName) || (currentStep !== nextProps.currentStep && currentStep === step)) {
      this.checkFormError();
    }
    if (this.props.isReadOnly && nextProps.errors !== this.props.errors) {
      this.state.validationCounter += 1;
      this.validatorGroup.checkValidation();
    }
    if (!nextProps.loading) {
      this.clickedItemId = '';
    }
  }

  onResize = () => {
    const { adjust } = this.props;
    if (adjust) adjust();
  };

  handleNext = () => {
    const { submitted } = this.props;
    if (submitted) {
      this.props.next();
      return;
    }
    const valid = this.validatorGroup.checkValidation();
    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      this.setState({ validationCounter: this.state.validationCounter + 1 });
    } else {
      this.props.makeValid(this.props.step);
    }
    this.handleFormDataSave();
    this.props.next();
  };

  handleFormDataSave = () => {
    this.props.onFormDataSave();
    this.props.setFormHasChanges(false);
  };

  handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Tab') return;
    if (e.key === 'Tab') e.preventDefault();
  };

  handleSaveButtonClick = (event) => {
    const target = event.currentTarget || event.srcElement;
    this.clickedItemId = target.id;
    this.handleFormDataSave();
    if (this.nextButton) this.nextButton.focus();
  };

  showSpinnerForElement = (elementId) => this.clickedItemId === elementId;

  checkFormError() {
    const valid = this.validatorGroup.checkValidation();
    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      this.setState({ validationCounter: this.state.validationCounter + 1 });
    } else {
      this.props.makeValid(this.props.step);
    }
  }

  EXPENSE_TYPE_KEYS = [
    'health',
    'pregnancyAndAdoption',
    'employmentAndEducation',
    'legalIssues',
    'cancellationsDelaysCarrierWeather',
    'otherReasons',
  ];

  EXPENSE_TYPE_MESSAGES = {
    health: messages.expenseTypeHealth,
    pregnancyAndAdoption: messages.expenseTypePregnancyAndAdoption,
    employmentAndEducation: messages.expenseTypeEmploymentAndEducation,
    legalIssues: messages.expenseTypeLegalIssues,
    cancellationsDelaysCarrierWeather: messages.expenseTypeCancellationsDelaysCarrierWeather,
    otherReasons: messages.expenseTypeOtherReasons,
  };

  HEALTH_CLAIM_FIELDS = [
    'healthClaimSelfOrCompanion',
    'healthClaimFamilyMember',
    'healthClaimBusinessPartnerEtc',
    'healthClaimHostAtDestination',
  ];

  PREGNANCY_ADOPTION_CLAIM_FIELDS = [
    'pregnancyAdoptionClaimLegalAdoption',
  ];

  PREGNANCY_ADOPTION_RESET_FIELDS = [
    'travelCostsTransportPregnancyAdoption',
    'travelCostsNonRefundPrepaidAirfarePregnancyAdoption',
    'tripInterruptionPregnancyAdoptionNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesPregnancyAdoption',
    'tripInterruptionPregnancyAdoptionChangeFeesItems',
    'travelCostsAirfareReturnPointPregnancyAdoption',
    'tripInterruptionPregnancyAdoptionAirfareReturnPointItems',
    'travelCostsSeatUpgradePregnancyAdoption',
    'tripInterruptionPregnancyAdoptionSeatUpgradeItems',
    'travelCostsTransportCatchUpPregnancyAdoption',
    'tripInterruptionPregnancyAdoptionTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherPregnancyAdoption',
    'tripInterruptionPregnancyAdoptionNonRefundTravelOtherItems',
    'travelCostsSingleSupplementPregnancyAdoption',
    'tripInterruptionPregnancyAdoptionSingleSupplementItems',
    'travelCostsOutOfPocketPregnancyAdoption',
    'tripInterruptionPregnancyAdoptionOutOfPocketItems',
  ];

  PREGNANCY_ADOPTION_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfarePregnancyAdoption',
    'tripInterruptionPregnancyAdoptionNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesPregnancyAdoption',
    'tripInterruptionPregnancyAdoptionChangeFeesItems',
    'travelCostsAirfareReturnPointPregnancyAdoption',
    'tripInterruptionPregnancyAdoptionAirfareReturnPointItems',
    'travelCostsSeatUpgradePregnancyAdoption',
    'tripInterruptionPregnancyAdoptionSeatUpgradeItems',
    'travelCostsTransportCatchUpPregnancyAdoption',
    'tripInterruptionPregnancyAdoptionTransportCatchUpItems',
  ];

  PREGNANCY_ADOPTION_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionPregnancyAdoptionNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfarePregnancyAdoption', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionPregnancyAdoptionChangeFeesItems', fieldKey: 'travelCostsChangeFeesPregnancyAdoption', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionPregnancyAdoptionAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointPregnancyAdoption', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionPregnancyAdoptionSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradePregnancyAdoption', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionPregnancyAdoptionTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpPregnancyAdoption', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  PREGNANCY_ADOPTION_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionPregnancyAdoptionNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherPregnancyAdoption', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionPregnancyAdoptionSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementPregnancyAdoption', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionPregnancyAdoptionOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketPregnancyAdoption', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  EMPLOYMENT_EDUCATION_CLAIM_FIELDS = [
    'employmentEducationClaimJobTransfer',
    'employmentEducationClaimInvoluntaryLossEmployment',
    'employmentEducationClaimBusinessMeetingCancellation',
    'employmentEducationClaimConferenceCancellation',
  ];

  EMPLOYMENT_EDUCATION_JOB_TRANSFER_RESET_FIELDS = [
    'travelCostsTransportEmploymentJobTransfer',
    'travelCostsNonRefundPrepaidAirfareEmploymentJobTransfer',
    'tripInterruptionEmploymentJobTransferNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesEmploymentJobTransfer',
    'tripInterruptionEmploymentJobTransferChangeFeesItems',
    'travelCostsAirfareReturnPointEmploymentJobTransfer',
    'tripInterruptionEmploymentJobTransferAirfareReturnPointItems',
    'travelCostsSeatUpgradeEmploymentJobTransfer',
    'tripInterruptionEmploymentJobTransferSeatUpgradeItems',
    'travelCostsTransportCatchUpEmploymentJobTransfer',
    'tripInterruptionEmploymentJobTransferTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherEmploymentJobTransfer',
    'tripInterruptionEmploymentJobTransferNonRefundTravelOtherItems',
    'travelCostsSingleSupplementEmploymentJobTransfer',
    'tripInterruptionEmploymentJobTransferSingleSupplementItems',
    'travelCostsOutOfPocketEmploymentJobTransfer',
    'tripInterruptionEmploymentJobTransferOutOfPocketItems',
  ];

  EMPLOYMENT_EDUCATION_INVOLUNTARY_LOSS_RESET_FIELDS = [
    'travelCostsTransportEmploymentInvoluntaryLoss',
    'travelCostsNonRefundPrepaidAirfareEmploymentInvoluntaryLoss',
    'tripInterruptionEmploymentInvoluntaryLossNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesEmploymentInvoluntaryLoss',
    'tripInterruptionEmploymentInvoluntaryLossChangeFeesItems',
    'travelCostsAirfareReturnPointEmploymentInvoluntaryLoss',
    'tripInterruptionEmploymentInvoluntaryLossAirfareReturnPointItems',
    'travelCostsSeatUpgradeEmploymentInvoluntaryLoss',
    'tripInterruptionEmploymentInvoluntaryLossSeatUpgradeItems',
    'travelCostsTransportCatchUpEmploymentInvoluntaryLoss',
    'tripInterruptionEmploymentInvoluntaryLossTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherEmploymentInvoluntaryLoss',
    'tripInterruptionEmploymentInvoluntaryLossNonRefundTravelOtherItems',
    'travelCostsSingleSupplementEmploymentInvoluntaryLoss',
    'tripInterruptionEmploymentInvoluntaryLossSingleSupplementItems',
    'travelCostsOutOfPocketEmploymentInvoluntaryLoss',
    'tripInterruptionEmploymentInvoluntaryLossOutOfPocketItems',
  ];

  EMPLOYMENT_EDUCATION_BUSINESS_MEETING_RESET_FIELDS = [
    'travelCostsTransportEmploymentBusinessMeeting',
    'travelCostsNonRefundPrepaidAirfareEmploymentBusinessMeeting',
    'tripInterruptionEmploymentBusinessMeetingNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesEmploymentBusinessMeeting',
    'tripInterruptionEmploymentBusinessMeetingChangeFeesItems',
    'travelCostsAirfareReturnPointEmploymentBusinessMeeting',
    'tripInterruptionEmploymentBusinessMeetingAirfareReturnPointItems',
    'travelCostsSeatUpgradeEmploymentBusinessMeeting',
    'tripInterruptionEmploymentBusinessMeetingSeatUpgradeItems',
    'travelCostsTransportCatchUpEmploymentBusinessMeeting',
    'tripInterruptionEmploymentBusinessMeetingTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherEmploymentBusinessMeeting',
    'tripInterruptionEmploymentBusinessMeetingNonRefundTravelOtherItems',
    'travelCostsSingleSupplementEmploymentBusinessMeeting',
    'tripInterruptionEmploymentBusinessMeetingSingleSupplementItems',
    'travelCostsOutOfPocketEmploymentBusinessMeeting',
    'tripInterruptionEmploymentBusinessMeetingOutOfPocketItems',
  ];

  EMPLOYMENT_EDUCATION_CONFERENCE_RESET_FIELDS = [
    'travelCostsTransportEmploymentConference',
    'travelCostsNonRefundPrepaidAirfareEmploymentConference',
    'tripInterruptionEmploymentConferenceNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesEmploymentConference',
    'tripInterruptionEmploymentConferenceChangeFeesItems',
    'travelCostsAirfareReturnPointEmploymentConference',
    'tripInterruptionEmploymentConferenceAirfareReturnPointItems',
    'travelCostsSeatUpgradeEmploymentConference',
    'tripInterruptionEmploymentConferenceSeatUpgradeItems',
    'travelCostsTransportCatchUpEmploymentConference',
    'tripInterruptionEmploymentConferenceTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherEmploymentConference',
    'tripInterruptionEmploymentConferenceNonRefundTravelOtherItems',
    'travelCostsSingleSupplementEmploymentConference',
    'tripInterruptionEmploymentConferenceSingleSupplementItems',
    'travelCostsOutOfPocketEmploymentConference',
    'tripInterruptionEmploymentConferenceOutOfPocketItems',
  ];

  EMPLOYMENT_EDUCATION_RESET_FIELDS = [
    ...this.EMPLOYMENT_EDUCATION_CLAIM_FIELDS,
    ...this.EMPLOYMENT_EDUCATION_JOB_TRANSFER_RESET_FIELDS,
    ...this.EMPLOYMENT_EDUCATION_INVOLUNTARY_LOSS_RESET_FIELDS,
    ...this.EMPLOYMENT_EDUCATION_BUSINESS_MEETING_RESET_FIELDS,
    ...this.EMPLOYMENT_EDUCATION_CONFERENCE_RESET_FIELDS,
  ];

  EMPLOYMENT_JOB_TRANSFER_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareEmploymentJobTransfer',
    'tripInterruptionEmploymentJobTransferNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesEmploymentJobTransfer',
    'tripInterruptionEmploymentJobTransferChangeFeesItems',
    'travelCostsAirfareReturnPointEmploymentJobTransfer',
    'tripInterruptionEmploymentJobTransferAirfareReturnPointItems',
    'travelCostsSeatUpgradeEmploymentJobTransfer',
    'tripInterruptionEmploymentJobTransferSeatUpgradeItems',
    'travelCostsTransportCatchUpEmploymentJobTransfer',
    'tripInterruptionEmploymentJobTransferTransportCatchUpItems',
  ];

  EMPLOYMENT_INVOLUNTARY_LOSS_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareEmploymentInvoluntaryLoss',
    'tripInterruptionEmploymentInvoluntaryLossNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesEmploymentInvoluntaryLoss',
    'tripInterruptionEmploymentInvoluntaryLossChangeFeesItems',
    'travelCostsAirfareReturnPointEmploymentInvoluntaryLoss',
    'tripInterruptionEmploymentInvoluntaryLossAirfareReturnPointItems',
    'travelCostsSeatUpgradeEmploymentInvoluntaryLoss',
    'tripInterruptionEmploymentInvoluntaryLossSeatUpgradeItems',
    'travelCostsTransportCatchUpEmploymentInvoluntaryLoss',
    'tripInterruptionEmploymentInvoluntaryLossTransportCatchUpItems',
  ];

  EMPLOYMENT_BUSINESS_MEETING_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareEmploymentBusinessMeeting',
    'tripInterruptionEmploymentBusinessMeetingNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesEmploymentBusinessMeeting',
    'tripInterruptionEmploymentBusinessMeetingChangeFeesItems',
    'travelCostsAirfareReturnPointEmploymentBusinessMeeting',
    'tripInterruptionEmploymentBusinessMeetingAirfareReturnPointItems',
    'travelCostsSeatUpgradeEmploymentBusinessMeeting',
    'tripInterruptionEmploymentBusinessMeetingSeatUpgradeItems',
    'travelCostsTransportCatchUpEmploymentBusinessMeeting',
    'tripInterruptionEmploymentBusinessMeetingTransportCatchUpItems',
  ];

  EMPLOYMENT_CONFERENCE_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareEmploymentConference',
    'tripInterruptionEmploymentConferenceNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesEmploymentConference',
    'tripInterruptionEmploymentConferenceChangeFeesItems',
    'travelCostsAirfareReturnPointEmploymentConference',
    'tripInterruptionEmploymentConferenceAirfareReturnPointItems',
    'travelCostsSeatUpgradeEmploymentConference',
    'tripInterruptionEmploymentConferenceSeatUpgradeItems',
    'travelCostsTransportCatchUpEmploymentConference',
    'tripInterruptionEmploymentConferenceTransportCatchUpItems',
  ];

  EMPLOYMENT_JOB_TRANSFER_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionEmploymentJobTransferNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareEmploymentJobTransfer', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentJobTransferChangeFeesItems', fieldKey: 'travelCostsChangeFeesEmploymentJobTransfer', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentJobTransferAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointEmploymentJobTransfer', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentJobTransferSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeEmploymentJobTransfer', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentJobTransferTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpEmploymentJobTransfer', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  EMPLOYMENT_JOB_TRANSFER_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionEmploymentJobTransferNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherEmploymentJobTransfer', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentJobTransferSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementEmploymentJobTransfer', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionEmploymentJobTransferOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketEmploymentJobTransfer', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  EMPLOYMENT_INVOLUNTARY_LOSS_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionEmploymentInvoluntaryLossNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareEmploymentInvoluntaryLoss', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentInvoluntaryLossChangeFeesItems', fieldKey: 'travelCostsChangeFeesEmploymentInvoluntaryLoss', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentInvoluntaryLossAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointEmploymentInvoluntaryLoss', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentInvoluntaryLossSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeEmploymentInvoluntaryLoss', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentInvoluntaryLossTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpEmploymentInvoluntaryLoss', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  EMPLOYMENT_INVOLUNTARY_LOSS_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionEmploymentInvoluntaryLossNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherEmploymentInvoluntaryLoss', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentInvoluntaryLossSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementEmploymentInvoluntaryLoss', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionEmploymentInvoluntaryLossOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketEmploymentInvoluntaryLoss', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  EMPLOYMENT_BUSINESS_MEETING_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionEmploymentBusinessMeetingNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareEmploymentBusinessMeeting', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentBusinessMeetingChangeFeesItems', fieldKey: 'travelCostsChangeFeesEmploymentBusinessMeeting', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentBusinessMeetingAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointEmploymentBusinessMeeting', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentBusinessMeetingSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeEmploymentBusinessMeeting', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentBusinessMeetingTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpEmploymentBusinessMeeting', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  EMPLOYMENT_BUSINESS_MEETING_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionEmploymentBusinessMeetingNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherEmploymentBusinessMeeting', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentBusinessMeetingSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementEmploymentBusinessMeeting', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionEmploymentBusinessMeetingOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketEmploymentBusinessMeeting', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  EMPLOYMENT_CONFERENCE_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionEmploymentConferenceNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareEmploymentConference', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentConferenceChangeFeesItems', fieldKey: 'travelCostsChangeFeesEmploymentConference', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentConferenceAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointEmploymentConference', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentConferenceSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeEmploymentConference', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentConferenceTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpEmploymentConference', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  EMPLOYMENT_CONFERENCE_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionEmploymentConferenceNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherEmploymentConference', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionEmploymentConferenceSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementEmploymentConference', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionEmploymentConferenceOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketEmploymentConference', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  LEGAL_ISSUES_CLAIM_FIELDS = [
    'legalIssuesClaimJuryDuty',
    'legalIssuesClaimMilitaryService',
    'legalIssuesClaimTravelAdvisory',
  ];

  LEGAL_ISSUES_JURY_DUTY_RESET_FIELDS = [
    'travelCostsTransportLegalIssuesJuryDuty',
    'travelCostsNonRefundPrepaidAirfareLegalIssuesJuryDuty',
    'tripInterruptionLegalIssuesJuryDutyNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesLegalIssuesJuryDuty',
    'tripInterruptionLegalIssuesJuryDutyChangeFeesItems',
    'travelCostsAirfareReturnPointLegalIssuesJuryDuty',
    'tripInterruptionLegalIssuesJuryDutyAirfareReturnPointItems',
    'travelCostsSeatUpgradeLegalIssuesJuryDuty',
    'tripInterruptionLegalIssuesJuryDutySeatUpgradeItems',
    'travelCostsTransportCatchUpLegalIssuesJuryDuty',
    'tripInterruptionLegalIssuesJuryDutyTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherLegalIssuesJuryDuty',
    'tripInterruptionLegalIssuesJuryDutyNonRefundTravelOtherItems',
    'travelCostsSingleSupplementLegalIssuesJuryDuty',
    'tripInterruptionLegalIssuesJuryDutySingleSupplementItems',
    'travelCostsOutOfPocketLegalIssuesJuryDuty',
    'tripInterruptionLegalIssuesJuryDutyOutOfPocketItems',
  ];

  LEGAL_ISSUES_MILITARY_SERVICE_RESET_FIELDS = [
    'travelCostsTransportLegalIssuesMilitaryService',
    'travelCostsNonRefundPrepaidAirfareLegalIssuesMilitaryService',
    'tripInterruptionLegalIssuesMilitaryServiceNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesLegalIssuesMilitaryService',
    'tripInterruptionLegalIssuesMilitaryServiceChangeFeesItems',
    'travelCostsAirfareReturnPointLegalIssuesMilitaryService',
    'tripInterruptionLegalIssuesMilitaryServiceAirfareReturnPointItems',
    'travelCostsSeatUpgradeLegalIssuesMilitaryService',
    'tripInterruptionLegalIssuesMilitaryServiceSeatUpgradeItems',
    'travelCostsTransportCatchUpLegalIssuesMilitaryService',
    'tripInterruptionLegalIssuesMilitaryServiceTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherLegalIssuesMilitaryService',
    'tripInterruptionLegalIssuesMilitaryServiceNonRefundTravelOtherItems',
    'travelCostsSingleSupplementLegalIssuesMilitaryService',
    'tripInterruptionLegalIssuesMilitaryServiceSingleSupplementItems',
    'travelCostsOutOfPocketLegalIssuesMilitaryService',
    'tripInterruptionLegalIssuesMilitaryServiceOutOfPocketItems',
  ];

  LEGAL_ISSUES_TRAVEL_ADVISORY_RESET_FIELDS = [
    'travelCostsTransportLegalIssuesTravelAdvisory',
    'travelCostsNonRefundPrepaidAirfareLegalIssuesTravelAdvisory',
    'tripInterruptionLegalIssuesTravelAdvisoryNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesLegalIssuesTravelAdvisory',
    'tripInterruptionLegalIssuesTravelAdvisoryChangeFeesItems',
    'travelCostsAirfareReturnPointLegalIssuesTravelAdvisory',
    'tripInterruptionLegalIssuesTravelAdvisoryAirfareReturnPointItems',
    'travelCostsSeatUpgradeLegalIssuesTravelAdvisory',
    'tripInterruptionLegalIssuesTravelAdvisorySeatUpgradeItems',
    'travelCostsTransportCatchUpLegalIssuesTravelAdvisory',
    'tripInterruptionLegalIssuesTravelAdvisoryTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherLegalIssuesTravelAdvisory',
    'tripInterruptionLegalIssuesTravelAdvisoryNonRefundTravelOtherItems',
    'travelCostsSingleSupplementLegalIssuesTravelAdvisory',
    'tripInterruptionLegalIssuesTravelAdvisorySingleSupplementItems',
    'travelCostsOutOfPocketLegalIssuesTravelAdvisory',
    'tripInterruptionLegalIssuesTravelAdvisoryOutOfPocketItems',
  ];

  LEGAL_ISSUES_RESET_FIELDS = [
    ...this.LEGAL_ISSUES_CLAIM_FIELDS,
    ...this.LEGAL_ISSUES_JURY_DUTY_RESET_FIELDS,
    ...this.LEGAL_ISSUES_MILITARY_SERVICE_RESET_FIELDS,
    ...this.LEGAL_ISSUES_TRAVEL_ADVISORY_RESET_FIELDS,
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_CLAIM_FIELDS = [
    'cancellationsDelaysCarrierWeatherClaimMissedConnection',
    'cancellationsDelaysCarrierWeatherClaimFourHoursNoConnection',
    'cancellationsDelaysCarrierWeatherClaimCancellationOfCommonCarrier',
    'cancellationsDelaysCarrierWeatherClaimTourOperatorCancellation',
    'cancellationsDelaysCarrierWeatherClaimAccidentOnWay',
    'cancellationsDelaysCarrierWeatherClaimDelayOfPrivateVehicle',
  ];

  OTHER_REASONS_CLAIM_FIELDS = [
    'otherReasonsClaimNaturalDisaster',
    'otherReasonsClaimUnforeseeableEvent',
  ];

  OTHER_REASONS_NATURAL_DISASTER_RESET_FIELDS = [
    'travelCostsTransportOtherReasonsNaturalDisaster',
    'travelCostsNonRefundPrepaidAirfareOtherReasonsNaturalDisaster',
    'tripInterruptionOtherReasonsNaturalDisasterNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesOtherReasonsNaturalDisaster',
    'tripInterruptionOtherReasonsNaturalDisasterChangeFeesItems',
    'travelCostsAirfareReturnPointOtherReasonsNaturalDisaster',
    'tripInterruptionOtherReasonsNaturalDisasterAirfareReturnPointItems',
    'travelCostsSeatUpgradeOtherReasonsNaturalDisaster',
    'tripInterruptionOtherReasonsNaturalDisasterSeatUpgradeItems',
    'travelCostsTransportCatchUpOtherReasonsNaturalDisaster',
    'tripInterruptionOtherReasonsNaturalDisasterTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherOtherReasonsNaturalDisaster',
    'tripInterruptionOtherReasonsNaturalDisasterNonRefundTravelOtherItems',
    'travelCostsSingleSupplementOtherReasonsNaturalDisaster',
    'tripInterruptionOtherReasonsNaturalDisasterSingleSupplementItems',
    'travelCostsOutOfPocketOtherReasonsNaturalDisaster',
    'tripInterruptionOtherReasonsNaturalDisasterOutOfPocketItems',
  ];

  OTHER_REASONS_NATURAL_DISASTER_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareOtherReasonsNaturalDisaster',
    'tripInterruptionOtherReasonsNaturalDisasterNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesOtherReasonsNaturalDisaster',
    'tripInterruptionOtherReasonsNaturalDisasterChangeFeesItems',
    'travelCostsAirfareReturnPointOtherReasonsNaturalDisaster',
    'tripInterruptionOtherReasonsNaturalDisasterAirfareReturnPointItems',
    'travelCostsSeatUpgradeOtherReasonsNaturalDisaster',
    'tripInterruptionOtherReasonsNaturalDisasterSeatUpgradeItems',
    'travelCostsTransportCatchUpOtherReasonsNaturalDisaster',
    'tripInterruptionOtherReasonsNaturalDisasterTransportCatchUpItems',
  ];

  OTHER_REASONS_NATURAL_DISASTER_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionOtherReasonsNaturalDisasterNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareOtherReasonsNaturalDisaster', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionOtherReasonsNaturalDisasterChangeFeesItems', fieldKey: 'travelCostsChangeFeesOtherReasonsNaturalDisaster', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionOtherReasonsNaturalDisasterAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointOtherReasonsNaturalDisaster', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionOtherReasonsNaturalDisasterSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeOtherReasonsNaturalDisaster', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionOtherReasonsNaturalDisasterTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpOtherReasonsNaturalDisaster', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  OTHER_REASONS_NATURAL_DISASTER_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionOtherReasonsNaturalDisasterNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherOtherReasonsNaturalDisaster', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionOtherReasonsNaturalDisasterSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementOtherReasonsNaturalDisaster', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionOtherReasonsNaturalDisasterOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketOtherReasonsNaturalDisaster', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  OTHER_REASONS_UNFORESEEABLE_EVENT_RESET_FIELDS = [
    'travelCostsTransportOtherReasonsUnforeseeableEvent',
    'travelCostsNonRefundPrepaidAirfareOtherReasonsUnforeseeableEvent',
    'tripInterruptionOtherReasonsUnforeseeableEventNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesOtherReasonsUnforeseeableEvent',
    'tripInterruptionOtherReasonsUnforeseeableEventChangeFeesItems',
    'travelCostsAirfareReturnPointOtherReasonsUnforeseeableEvent',
    'tripInterruptionOtherReasonsUnforeseeableEventAirfareReturnPointItems',
    'travelCostsSeatUpgradeOtherReasonsUnforeseeableEvent',
    'tripInterruptionOtherReasonsUnforeseeableEventSeatUpgradeItems',
    'travelCostsTransportCatchUpOtherReasonsUnforeseeableEvent',
    'tripInterruptionOtherReasonsUnforeseeableEventTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherOtherReasonsUnforeseeableEvent',
    'tripInterruptionOtherReasonsUnforeseeableEventNonRefundTravelOtherItems',
    'travelCostsSingleSupplementOtherReasonsUnforeseeableEvent',
    'tripInterruptionOtherReasonsUnforeseeableEventSingleSupplementItems',
    'travelCostsOutOfPocketOtherReasonsUnforeseeableEvent',
    'tripInterruptionOtherReasonsUnforeseeableEventOutOfPocketItems',
  ];

  OTHER_REASONS_UNFORESEEABLE_EVENT_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareOtherReasonsUnforeseeableEvent',
    'tripInterruptionOtherReasonsUnforeseeableEventNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesOtherReasonsUnforeseeableEvent',
    'tripInterruptionOtherReasonsUnforeseeableEventChangeFeesItems',
    'travelCostsAirfareReturnPointOtherReasonsUnforeseeableEvent',
    'tripInterruptionOtherReasonsUnforeseeableEventAirfareReturnPointItems',
    'travelCostsSeatUpgradeOtherReasonsUnforeseeableEvent',
    'tripInterruptionOtherReasonsUnforeseeableEventSeatUpgradeItems',
    'travelCostsTransportCatchUpOtherReasonsUnforeseeableEvent',
    'tripInterruptionOtherReasonsUnforeseeableEventTransportCatchUpItems',
  ];

  OTHER_REASONS_UNFORESEEABLE_EVENT_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionOtherReasonsUnforeseeableEventNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareOtherReasonsUnforeseeableEvent', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionOtherReasonsUnforeseeableEventChangeFeesItems', fieldKey: 'travelCostsChangeFeesOtherReasonsUnforeseeableEvent', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionOtherReasonsUnforeseeableEventAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointOtherReasonsUnforeseeableEvent', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionOtherReasonsUnforeseeableEventSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeOtherReasonsUnforeseeableEvent', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionOtherReasonsUnforeseeableEventTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpOtherReasonsUnforeseeableEvent', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  OTHER_REASONS_UNFORESEEABLE_EVENT_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionOtherReasonsUnforeseeableEventNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherOtherReasonsUnforeseeableEvent', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionOtherReasonsUnforeseeableEventSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementOtherReasonsUnforeseeableEvent', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionOtherReasonsUnforeseeableEventOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketOtherReasonsUnforeseeableEvent', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_MISSED_CONNECTION_RESET_FIELDS = [
    'travelCostsTransportCancellationsDelaysCarrierWeatherMissedConnection',
    'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherMissedConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesCancellationsDelaysCarrierWeatherMissedConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionChangeFeesItems',
    'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherMissedConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionAirfareReturnPointItems',
    'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherMissedConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionSeatUpgradeItems',
    'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherMissedConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherCancellationsDelaysCarrierWeatherMissedConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionNonRefundTravelOtherItems',
    'travelCostsSingleSupplementCancellationsDelaysCarrierWeatherMissedConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionSingleSupplementItems',
    'travelCostsOutOfPocketCancellationsDelaysCarrierWeatherMissedConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionOutOfPocketItems',
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_MISSED_CONNECTION_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherMissedConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesCancellationsDelaysCarrierWeatherMissedConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionChangeFeesItems',
    'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherMissedConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionAirfareReturnPointItems',
    'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherMissedConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionSeatUpgradeItems',
    'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherMissedConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionTransportCatchUpItems',
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_MISSED_CONNECTION_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherMissedConnection', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionChangeFeesItems', fieldKey: 'travelCostsChangeFeesCancellationsDelaysCarrierWeatherMissedConnection', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherMissedConnection', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherMissedConnection', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherMissedConnection', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_MISSED_CONNECTION_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherCancellationsDelaysCarrierWeatherMissedConnection', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementCancellationsDelaysCarrierWeatherMissedConnection', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherMissedConnectionOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketCancellationsDelaysCarrierWeatherMissedConnection', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_FOUR_HOURS_NO_CONNECTION_RESET_FIELDS = [
    'travelCostsTransportCancellationsDelaysCarrierWeatherFourHoursNoConnection',
    'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherFourHoursNoConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesCancellationsDelaysCarrierWeatherFourHoursNoConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionChangeFeesItems',
    'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherFourHoursNoConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionAirfareReturnPointItems',
    'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherFourHoursNoConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionSeatUpgradeItems',
    'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherFourHoursNoConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherCancellationsDelaysCarrierWeatherFourHoursNoConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionNonRefundTravelOtherItems',
    'travelCostsSingleSupplementCancellationsDelaysCarrierWeatherFourHoursNoConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionSingleSupplementItems',
    'travelCostsOutOfPocketCancellationsDelaysCarrierWeatherFourHoursNoConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionOutOfPocketItems',
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_FOUR_HOURS_NO_CONNECTION_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherFourHoursNoConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesCancellationsDelaysCarrierWeatherFourHoursNoConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionChangeFeesItems',
    'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherFourHoursNoConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionAirfareReturnPointItems',
    'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherFourHoursNoConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionSeatUpgradeItems',
    'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherFourHoursNoConnection',
    'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionTransportCatchUpItems',
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_FOUR_HOURS_NO_CONNECTION_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherFourHoursNoConnection', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionChangeFeesItems', fieldKey: 'travelCostsChangeFeesCancellationsDelaysCarrierWeatherFourHoursNoConnection', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherFourHoursNoConnection', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherFourHoursNoConnection', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherFourHoursNoConnection', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_FOUR_HOURS_NO_CONNECTION_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherCancellationsDelaysCarrierWeatherFourHoursNoConnection', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementCancellationsDelaysCarrierWeatherFourHoursNoConnection', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherFourHoursNoConnectionOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketCancellationsDelaysCarrierWeatherFourHoursNoConnection', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_CANCELLATION_OF_COMMON_CARRIER_RESET_FIELDS = [
    'travelCostsTransportCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier',
    'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier',
    'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier',
    'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierChangeFeesItems',
    'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier',
    'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierAirfareReturnPointItems',
    'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier',
    'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierSeatUpgradeItems',
    'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier',
    'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier',
    'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierNonRefundTravelOtherItems',
    'travelCostsSingleSupplementCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier',
    'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierSingleSupplementItems',
    'travelCostsOutOfPocketCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier',
    'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierOutOfPocketItems',
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_CANCELLATION_OF_COMMON_CARRIER_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier',
    'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier',
    'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierChangeFeesItems',
    'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier',
    'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierAirfareReturnPointItems',
    'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier',
    'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierSeatUpgradeItems',
    'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier',
    'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierTransportCatchUpItems',
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_CANCELLATION_OF_COMMON_CARRIER_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierChangeFeesItems', fieldKey: 'travelCostsChangeFeesCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_CANCELLATION_OF_COMMON_CARRIER_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherCancellationOfCommonCarrierOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_TOUR_OPERATOR_CANCELLATION_RESET_FIELDS = [
    'travelCostsNonRefundableUnusedTourCostCancellationsDelaysCarrierWeatherTourOperatorCancellation',
    'tripInterruptionCancellationsDelaysCarrierWeatherTourOperatorCancellationNonRefundableUnusedTourCostItems',
    'travelCostsReturnToDeparturePointCancellationsDelaysCarrierWeatherTourOperatorCancellation',
    'tripInterruptionCancellationsDelaysCarrierWeatherTourOperatorCancellationReturnToDeparturePointItems',
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_TOUR_OPERATOR_CANCELLATION_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherTourOperatorCancellationNonRefundableUnusedTourCostItems', fieldKey: 'travelCostsNonRefundableUnusedTourCostCancellationsDelaysCarrierWeatherTourOperatorCancellation', label: messages.nonRefundableUnusedTourCost, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherTourOperatorCancellationReturnToDeparturePointItems', fieldKey: 'travelCostsReturnToDeparturePointCancellationsDelaysCarrierWeatherTourOperatorCancellation', label: messages.returnToDeparturePoint, tooltip: messages.returnToDeparturePointTooltip },
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_ACCIDENT_ON_WAY_RESET_FIELDS = [
    'travelCostsTransportCancellationsDelaysCarrierWeatherAccidentOnWay',
    'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherAccidentOnWay',
    'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesCancellationsDelaysCarrierWeatherAccidentOnWay',
    'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayChangeFeesItems',
    'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherAccidentOnWay',
    'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayAirfareReturnPointItems',
    'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherAccidentOnWay',
    'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWaySeatUpgradeItems',
    'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherAccidentOnWay',
    'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherCancellationsDelaysCarrierWeatherAccidentOnWay',
    'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayNonRefundTravelOtherItems',
    'travelCostsSingleSupplementCancellationsDelaysCarrierWeatherAccidentOnWay',
    'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWaySingleSupplementItems',
    'travelCostsOutOfPocketCancellationsDelaysCarrierWeatherAccidentOnWay',
    'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayOutOfPocketItems',
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_ACCIDENT_ON_WAY_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherAccidentOnWay',
    'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesCancellationsDelaysCarrierWeatherAccidentOnWay',
    'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayChangeFeesItems',
    'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherAccidentOnWay',
    'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayAirfareReturnPointItems',
    'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherAccidentOnWay',
    'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWaySeatUpgradeItems',
    'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherAccidentOnWay',
    'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayTransportCatchUpItems',
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_ACCIDENT_ON_WAY_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherAccidentOnWay', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayChangeFeesItems', fieldKey: 'travelCostsChangeFeesCancellationsDelaysCarrierWeatherAccidentOnWay', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherAccidentOnWay', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWaySeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherAccidentOnWay', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherAccidentOnWay', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_ACCIDENT_ON_WAY_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherCancellationsDelaysCarrierWeatherAccidentOnWay', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWaySingleSupplementItems', fieldKey: 'travelCostsSingleSupplementCancellationsDelaysCarrierWeatherAccidentOnWay', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherAccidentOnWayOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketCancellationsDelaysCarrierWeatherAccidentOnWay', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_DELAY_OF_PRIVATE_VEHICLE_RESET_FIELDS = [
    'travelCostsTransportCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle',
    'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle',
    'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle',
    'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleChangeFeesItems',
    'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle',
    'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleAirfareReturnPointItems',
    'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle',
    'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleSeatUpgradeItems',
    'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle',
    'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleTransportCatchUpItems',
    'travelCostsNonRefundTravelOtherCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle',
    'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleNonRefundTravelOtherItems',
    'travelCostsSingleSupplementCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle',
    'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleSingleSupplementItems',
    'travelCostsOutOfPocketCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle',
    'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleOutOfPocketItems',
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_DELAY_OF_PRIVATE_VEHICLE_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle',
    'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle',
    'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleChangeFeesItems',
    'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle',
    'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleAirfareReturnPointItems',
    'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle',
    'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleSeatUpgradeItems',
    'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle',
    'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleTransportCatchUpItems',
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_DELAY_OF_PRIVATE_VEHICLE_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleChangeFeesItems', fieldKey: 'travelCostsChangeFeesCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  CANCELLATIONS_DELAYS_CARRIER_WEATHER_DELAY_OF_PRIVATE_VEHICLE_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionCancellationsDelaysCarrierWeatherDelayOfPrivateVehicleOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  LEGAL_ISSUES_JURY_DUTY_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareLegalIssuesJuryDuty',
    'tripInterruptionLegalIssuesJuryDutyNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesLegalIssuesJuryDuty',
    'tripInterruptionLegalIssuesJuryDutyChangeFeesItems',
    'travelCostsAirfareReturnPointLegalIssuesJuryDuty',
    'tripInterruptionLegalIssuesJuryDutyAirfareReturnPointItems',
    'travelCostsSeatUpgradeLegalIssuesJuryDuty',
    'tripInterruptionLegalIssuesJuryDutySeatUpgradeItems',
    'travelCostsTransportCatchUpLegalIssuesJuryDuty',
    'tripInterruptionLegalIssuesJuryDutyTransportCatchUpItems',
  ];

  LEGAL_ISSUES_MILITARY_SERVICE_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareLegalIssuesMilitaryService',
    'tripInterruptionLegalIssuesMilitaryServiceNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesLegalIssuesMilitaryService',
    'tripInterruptionLegalIssuesMilitaryServiceChangeFeesItems',
    'travelCostsAirfareReturnPointLegalIssuesMilitaryService',
    'tripInterruptionLegalIssuesMilitaryServiceAirfareReturnPointItems',
    'travelCostsSeatUpgradeLegalIssuesMilitaryService',
    'tripInterruptionLegalIssuesMilitaryServiceSeatUpgradeItems',
    'travelCostsTransportCatchUpLegalIssuesMilitaryService',
    'tripInterruptionLegalIssuesMilitaryServiceTransportCatchUpItems',
  ];

  LEGAL_ISSUES_TRAVEL_ADVISORY_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareLegalIssuesTravelAdvisory',
    'tripInterruptionLegalIssuesTravelAdvisoryNonRefundPrepaidAirfareItems',
    'travelCostsChangeFeesLegalIssuesTravelAdvisory',
    'tripInterruptionLegalIssuesTravelAdvisoryChangeFeesItems',
    'travelCostsAirfareReturnPointLegalIssuesTravelAdvisory',
    'tripInterruptionLegalIssuesTravelAdvisoryAirfareReturnPointItems',
    'travelCostsSeatUpgradeLegalIssuesTravelAdvisory',
    'tripInterruptionLegalIssuesTravelAdvisorySeatUpgradeItems',
    'travelCostsTransportCatchUpLegalIssuesTravelAdvisory',
    'tripInterruptionLegalIssuesTravelAdvisoryTransportCatchUpItems',
  ];

  LEGAL_ISSUES_JURY_DUTY_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionLegalIssuesJuryDutyNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareLegalIssuesJuryDuty', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesJuryDutyChangeFeesItems', fieldKey: 'travelCostsChangeFeesLegalIssuesJuryDuty', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesJuryDutyAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointLegalIssuesJuryDuty', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesJuryDutySeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeLegalIssuesJuryDuty', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesJuryDutyTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpLegalIssuesJuryDuty', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  LEGAL_ISSUES_JURY_DUTY_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionLegalIssuesJuryDutyNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherLegalIssuesJuryDuty', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesJuryDutySingleSupplementItems', fieldKey: 'travelCostsSingleSupplementLegalIssuesJuryDuty', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionLegalIssuesJuryDutyOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketLegalIssuesJuryDuty', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  LEGAL_ISSUES_MILITARY_SERVICE_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionLegalIssuesMilitaryServiceNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareLegalIssuesMilitaryService', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesMilitaryServiceChangeFeesItems', fieldKey: 'travelCostsChangeFeesLegalIssuesMilitaryService', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesMilitaryServiceAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointLegalIssuesMilitaryService', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesMilitaryServiceSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeLegalIssuesMilitaryService', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesMilitaryServiceTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpLegalIssuesMilitaryService', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  LEGAL_ISSUES_MILITARY_SERVICE_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionLegalIssuesMilitaryServiceNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherLegalIssuesMilitaryService', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesMilitaryServiceSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementLegalIssuesMilitaryService', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionLegalIssuesMilitaryServiceOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketLegalIssuesMilitaryService', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  LEGAL_ISSUES_TRAVEL_ADVISORY_TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionLegalIssuesTravelAdvisoryNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareLegalIssuesTravelAdvisory', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesTravelAdvisoryChangeFeesItems', fieldKey: 'travelCostsChangeFeesLegalIssuesTravelAdvisory', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesTravelAdvisoryAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointLegalIssuesTravelAdvisory', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesTravelAdvisorySeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeLegalIssuesTravelAdvisory', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesTravelAdvisoryTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpLegalIssuesTravelAdvisory', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  LEGAL_ISSUES_TRAVEL_ADVISORY_OTHER_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionLegalIssuesTravelAdvisoryNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherLegalIssuesTravelAdvisory', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionLegalIssuesTravelAdvisorySingleSupplementItems', fieldKey: 'travelCostsSingleSupplementLegalIssuesTravelAdvisory', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionLegalIssuesTravelAdvisoryOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketLegalIssuesTravelAdvisory', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  TRAVEL_COSTS_TRANSPORT_RESET_FIELDS = [
    'travelCostsTransport',
    'travelCostsNonRefundPrepaidAirfare',
    'travelCostsChangeFees',
    'travelCostsAirfareReturnPoint',
    'travelCostsSeatUpgrade',
    'travelCostsTransportCatchUp',
    'tripInterruptionNonRefundPrepaidAirfareItems',
    'tripInterruptionChangeFeesItems',
    'tripInterruptionAirfareReturnPointItems',
    'tripInterruptionSeatUpgradeItems',
    'tripInterruptionTransportCatchUpItems',
  ];

  HEALTH_SELF_COMPANION_OTHER_EXPENSE_RESET_FIELDS = [
    'travelCostsNonRefundTravelOther',
    'travelCostsSingleSupplement',
    'travelCostsOutOfPocket',
    'tripInterruptionNonRefundTravelOtherItems',
    'tripInterruptionSingleSupplementItems',
    'tripInterruptionOutOfPocketItems',
  ];

  DEATH_EXPENSE_RESET_FIELDS = [
    'expensesRelatedToDeath',
    'travelCostsReturnOfRemains',
    'travelCostsBurialCremation',
    'tripInterruptionReturnOfRemainsItems',
    'tripInterruptionBurialCremationItems',
  ];

  TRANSPORT_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfare', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionChangeFeesItems', fieldKey: 'travelCostsChangeFees', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPoint', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgrade', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUp', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  DEATH_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionReturnOfRemainsItems', fieldKey: 'travelCostsReturnOfRemains', label: messages.returnOfRemains, tooltip: messages.returnOfRemainsTooltip },
    { itemsKey: 'tripInterruptionBurialCremationItems', fieldKey: 'travelCostsBurialCremation', label: messages.burialCremationPlaceOfDeath, tooltip: messages.burialCremationPlaceOfDeathTooltip },
  ];

  OTHER_HEALTH_EXPENSE_CONFIGS = [
    { itemsKey: 'tripInterruptionNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOther', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionSingleSupplementItems', fieldKey: 'travelCostsSingleSupplement', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionOutOfPocketItems', fieldKey: 'travelCostsOutOfPocket', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  FAMILY_MEMBER_RESET_FIELDS = [
    'travelCostsTransportFamilyMember',
    'travelCostsNonRefundPrepaidAirfareFamilyMember',
    'travelCostsChangeFeesFamilyMember',
    'travelCostsAirfareReturnPointFamilyMember',
    'travelCostsSeatUpgradeFamilyMember',
    'travelCostsTransportCatchUpFamilyMember',
    'travelCostsNonRefundTravelOtherFamilyMember',
    'travelCostsSingleSupplementFamilyMember',
    'travelCostsOutOfPocketFamilyMember',
    'tripInterruptionFamilyMemberNonRefundPrepaidAirfareItems',
    'tripInterruptionFamilyMemberChangeFeesItems',
    'tripInterruptionFamilyMemberAirfareReturnPointItems',
    'tripInterruptionFamilyMemberSeatUpgradeItems',
    'tripInterruptionFamilyMemberTransportCatchUpItems',
    'tripInterruptionFamilyMemberNonRefundTravelOtherItems',
    'tripInterruptionFamilyMemberSingleSupplementItems',
    'tripInterruptionFamilyMemberOutOfPocketItems',
  ];

  TRANSPORT_EXPENSE_CONFIGS_FAMILY_MEMBER = [
    { itemsKey: 'tripInterruptionFamilyMemberNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareFamilyMember', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionFamilyMemberChangeFeesItems', fieldKey: 'travelCostsChangeFeesFamilyMember', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionFamilyMemberAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointFamilyMember', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionFamilyMemberSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeFamilyMember', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionFamilyMemberTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpFamilyMember', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  OTHER_HEALTH_EXPENSE_CONFIGS_FAMILY_MEMBER = [
    { itemsKey: 'tripInterruptionFamilyMemberNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherFamilyMember', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionFamilyMemberSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementFamilyMember', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionFamilyMemberOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketFamilyMember', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  BUSINESS_PARTNER_ETC_RESET_FIELDS = [
    'travelCostsTransportBusinessPartnerEtc',
    'travelCostsNonRefundPrepaidAirfareBusinessPartnerEtc',
    'travelCostsChangeFeesBusinessPartnerEtc',
    'travelCostsAirfareReturnPointBusinessPartnerEtc',
    'travelCostsSeatUpgradeBusinessPartnerEtc',
    'travelCostsTransportCatchUpBusinessPartnerEtc',
    'travelCostsNonRefundTravelOtherBusinessPartnerEtc',
    'travelCostsSingleSupplementBusinessPartnerEtc',
    'travelCostsOutOfPocketBusinessPartnerEtc',
    'tripInterruptionBusinessPartnerEtcNonRefundPrepaidAirfareItems',
    'tripInterruptionBusinessPartnerEtcChangeFeesItems',
    'tripInterruptionBusinessPartnerEtcAirfareReturnPointItems',
    'tripInterruptionBusinessPartnerEtcSeatUpgradeItems',
    'tripInterruptionBusinessPartnerEtcTransportCatchUpItems',
    'tripInterruptionBusinessPartnerEtcNonRefundTravelOtherItems',
    'tripInterruptionBusinessPartnerEtcSingleSupplementItems',
    'tripInterruptionBusinessPartnerEtcOutOfPocketItems',
  ];

  TRANSPORT_EXPENSE_CONFIGS_BUSINESS_PARTNER_ETC = [
    { itemsKey: 'tripInterruptionBusinessPartnerEtcNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareBusinessPartnerEtc', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionBusinessPartnerEtcChangeFeesItems', fieldKey: 'travelCostsChangeFeesBusinessPartnerEtc', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionBusinessPartnerEtcAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointBusinessPartnerEtc', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionBusinessPartnerEtcSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeBusinessPartnerEtc', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionBusinessPartnerEtcTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpBusinessPartnerEtc', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  OTHER_HEALTH_EXPENSE_CONFIGS_BUSINESS_PARTNER_ETC = [
    { itemsKey: 'tripInterruptionBusinessPartnerEtcNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherBusinessPartnerEtc', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionBusinessPartnerEtcSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementBusinessPartnerEtc', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionBusinessPartnerEtcOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketBusinessPartnerEtc', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  HOST_AT_DESTINATION_RESET_FIELDS = [
    'travelCostsTransportHostAtDestination',
    'travelCostsNonRefundPrepaidAirfareHostAtDestination',
    'travelCostsChangeFeesHostAtDestination',
    'travelCostsAirfareReturnPointHostAtDestination',
    'travelCostsSeatUpgradeHostAtDestination',
    'travelCostsTransportCatchUpHostAtDestination',
    'travelCostsNonRefundTravelOtherHostAtDestination',
    'travelCostsSingleSupplementHostAtDestination',
    'travelCostsOutOfPocketHostAtDestination',
    'tripInterruptionHostAtDestinationNonRefundPrepaidAirfareItems',
    'tripInterruptionHostAtDestinationChangeFeesItems',
    'tripInterruptionHostAtDestinationAirfareReturnPointItems',
    'tripInterruptionHostAtDestinationSeatUpgradeItems',
    'tripInterruptionHostAtDestinationTransportCatchUpItems',
    'tripInterruptionHostAtDestinationNonRefundTravelOtherItems',
    'tripInterruptionHostAtDestinationSingleSupplementItems',
    'tripInterruptionHostAtDestinationOutOfPocketItems',
  ];

  TRANSPORT_EXPENSE_CONFIGS_HOST_AT_DESTINATION = [
    { itemsKey: 'tripInterruptionHostAtDestinationNonRefundPrepaidAirfareItems', fieldKey: 'travelCostsNonRefundPrepaidAirfareHostAtDestination', label: messages.nonRefundablePrepaidAirfare, tooltip: null },
    { itemsKey: 'tripInterruptionHostAtDestinationChangeFeesItems', fieldKey: 'travelCostsChangeFeesHostAtDestination', label: messages.changeFees, tooltip: null },
    { itemsKey: 'tripInterruptionHostAtDestinationAirfareReturnPointItems', fieldKey: 'travelCostsAirfareReturnPointHostAtDestination', label: messages.airfareReturnDeparturePoint, tooltip: null },
    { itemsKey: 'tripInterruptionHostAtDestinationSeatUpgradeItems', fieldKey: 'travelCostsSeatUpgradeHostAtDestination', label: messages.medicallyNecessarySeatUpgrade, tooltip: null },
    { itemsKey: 'tripInterruptionHostAtDestinationTransportCatchUpItems', fieldKey: 'travelCostsTransportCatchUpHostAtDestination', label: messages.transportCatchUpDestination, tooltip: null },
  ];

  OTHER_HEALTH_EXPENSE_CONFIGS_HOST_AT_DESTINATION = [
    { itemsKey: 'tripInterruptionHostAtDestinationNonRefundTravelOtherItems', fieldKey: 'travelCostsNonRefundTravelOtherHostAtDestination', label: messages.nonRefundablePrepaidTravelOther, tooltip: null },
    { itemsKey: 'tripInterruptionHostAtDestinationSingleSupplementItems', fieldKey: 'travelCostsSingleSupplementHostAtDestination', label: messages.singleSupplement, tooltip: messages.singleSupplementTooltip },
    { itemsKey: 'tripInterruptionHostAtDestinationOutOfPocketItems', fieldKey: 'travelCostsOutOfPocketHostAtDestination', label: messages.outOfPocketExpenses, tooltip: messages.outOfPocketExpensesTooltip },
  ];

  BUSINESS_PARTNER_ETC_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareBusinessPartnerEtc',
    'travelCostsChangeFeesBusinessPartnerEtc',
    'travelCostsAirfareReturnPointBusinessPartnerEtc',
    'travelCostsSeatUpgradeBusinessPartnerEtc',
    'travelCostsTransportCatchUpBusinessPartnerEtc',
    'tripInterruptionBusinessPartnerEtcNonRefundPrepaidAirfareItems',
    'tripInterruptionBusinessPartnerEtcChangeFeesItems',
    'tripInterruptionBusinessPartnerEtcAirfareReturnPointItems',
    'tripInterruptionBusinessPartnerEtcSeatUpgradeItems',
    'tripInterruptionBusinessPartnerEtcTransportCatchUpItems',
  ];

  HOST_AT_DESTINATION_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareHostAtDestination',
    'travelCostsChangeFeesHostAtDestination',
    'travelCostsAirfareReturnPointHostAtDestination',
    'travelCostsSeatUpgradeHostAtDestination',
    'travelCostsTransportCatchUpHostAtDestination',
    'tripInterruptionHostAtDestinationNonRefundPrepaidAirfareItems',
    'tripInterruptionHostAtDestinationChangeFeesItems',
    'tripInterruptionHostAtDestinationAirfareReturnPointItems',
    'tripInterruptionHostAtDestinationSeatUpgradeItems',
    'tripInterruptionHostAtDestinationTransportCatchUpItems',
  ];

  handleExpenseTypesSelected = (propertyName) => (event) => {
    const { expenseTypesClaiming } = this.props.form;
    const selected = !!expenseTypesClaiming && typeof expenseTypesClaiming.toJS === 'function'
      ? expenseTypesClaiming.toJS()
      : expenseTypesClaiming || [];
    const listChecked = _.cloneDeep(selected);
    const target = event.target;
    const name = target.name;
    const checked = target.type === 'checkbox' ? target.checked : target.value;
    if (checked) {
      listChecked.push(name);
    } else {
      listChecked.splice(listChecked.indexOf(name), 1);
      if (name === 'health') {
        this.props.onResetFormCustom([
          ...this.HEALTH_CLAIM_FIELDS,
          ...this.TRAVEL_COSTS_TRANSPORT_RESET_FIELDS,
          ...this.HEALTH_SELF_COMPANION_OTHER_EXPENSE_RESET_FIELDS,
          ...this.DEATH_EXPENSE_RESET_FIELDS,
          ...this.FAMILY_MEMBER_RESET_FIELDS,
          ...this.BUSINESS_PARTNER_ETC_RESET_FIELDS,
          ...this.HOST_AT_DESTINATION_RESET_FIELDS,
        ]);
      }
      if (name === 'pregnancyAndAdoption') {
        this.props.onResetFormCustom([
          ...this.PREGNANCY_ADOPTION_CLAIM_FIELDS,
          ...this.PREGNANCY_ADOPTION_RESET_FIELDS,
        ]);
      }
      if (name === 'employmentAndEducation') {
        this.props.onResetFormCustom(this.EMPLOYMENT_EDUCATION_RESET_FIELDS);
      }
      if (name === 'legalIssues') {
        this.props.onResetFormCustom(this.LEGAL_ISSUES_RESET_FIELDS);
      }
      if (name === 'cancellationsDelaysCarrierWeather') {
        this.props.onResetFormCustom([
          ...this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_CLAIM_FIELDS,
          ...this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_MISSED_CONNECTION_RESET_FIELDS,
          ...this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_FOUR_HOURS_NO_CONNECTION_RESET_FIELDS,
          ...this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_CANCELLATION_OF_COMMON_CARRIER_RESET_FIELDS,
          ...this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_TOUR_OPERATOR_CANCELLATION_RESET_FIELDS,
          ...this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_ACCIDENT_ON_WAY_RESET_FIELDS,
          ...this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_DELAY_OF_PRIVATE_VEHICLE_RESET_FIELDS,
        ]);
      }
      if (name === 'otherReasons') {
        this.props.onResetFormCustom([
          ...this.OTHER_REASONS_CLAIM_FIELDS,
          ...this.OTHER_REASONS_NATURAL_DISASTER_RESET_FIELDS,
          ...this.OTHER_REASONS_UNFORESEEABLE_EVENT_RESET_FIELDS,
        ]);
      }
    }
    this.props.onFormDataChange(propertyName, listChecked);
    this.props.setFormHasChanges(true);
  };

  handlePregnancyAdoptionClaimChange = (propertyName) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (propertyName === 'form.pregnancyAdoptionClaimLegalAdoption' && !checked) {
      this.props.onResetFormCustom(this.PREGNANCY_ADOPTION_RESET_FIELDS);
    }
    this.props.onFormDataChange(propertyName, checked);
    this.props.setFormHasChanges(true);
  };

  handleTravelCostsTransportPregnancyAdoptionChange = (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom(this.PREGNANCY_ADOPTION_TRANSPORT_RESET_FIELDS);
    }
    this.props.onFormDataChange('form.travelCostsTransportPregnancyAdoption', checked);
    this.props.setFormHasChanges(true);
  };

  handleTransportSubOptionChangePregnancyAdoption = (fieldKey, itemsKey) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom([itemsKey]);
    }
    this.props.onFormDataChange(`form.${fieldKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  getEmploymentEducationResetFieldsByClaim = (claimFieldKey) => {
    switch (claimFieldKey) {
      case 'employmentEducationClaimJobTransfer': return this.EMPLOYMENT_EDUCATION_JOB_TRANSFER_RESET_FIELDS;
      case 'employmentEducationClaimInvoluntaryLossEmployment': return this.EMPLOYMENT_EDUCATION_INVOLUNTARY_LOSS_RESET_FIELDS;
      case 'employmentEducationClaimBusinessMeetingCancellation': return this.EMPLOYMENT_EDUCATION_BUSINESS_MEETING_RESET_FIELDS;
      case 'employmentEducationClaimConferenceCancellation': return this.EMPLOYMENT_EDUCATION_CONFERENCE_RESET_FIELDS;
      default: return [];
    }
  };

  getLegalIssuesResetFieldsByClaim = (claimFieldKey) => {
    switch (claimFieldKey) {
      case 'legalIssuesClaimJuryDuty': return this.LEGAL_ISSUES_JURY_DUTY_RESET_FIELDS;
      case 'legalIssuesClaimMilitaryService': return this.LEGAL_ISSUES_MILITARY_SERVICE_RESET_FIELDS;
      case 'legalIssuesClaimTravelAdvisory': return this.LEGAL_ISSUES_TRAVEL_ADVISORY_RESET_FIELDS;
      default: return [];
    }
  };

  getCancellationsDelaysCarrierWeatherResetFieldsByClaim = (claimFieldKey) => {
    switch (claimFieldKey) {
      case 'cancellationsDelaysCarrierWeatherClaimMissedConnection': return this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_MISSED_CONNECTION_RESET_FIELDS;
      case 'cancellationsDelaysCarrierWeatherClaimFourHoursNoConnection': return this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_FOUR_HOURS_NO_CONNECTION_RESET_FIELDS;
      case 'cancellationsDelaysCarrierWeatherClaimCancellationOfCommonCarrier': return this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_CANCELLATION_OF_COMMON_CARRIER_RESET_FIELDS;
      case 'cancellationsDelaysCarrierWeatherClaimTourOperatorCancellation': return this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_TOUR_OPERATOR_CANCELLATION_RESET_FIELDS;
      case 'cancellationsDelaysCarrierWeatherClaimAccidentOnWay': return this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_ACCIDENT_ON_WAY_RESET_FIELDS;
      case 'cancellationsDelaysCarrierWeatherClaimDelayOfPrivateVehicle': return this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_DELAY_OF_PRIVATE_VEHICLE_RESET_FIELDS;
      default: return [];
    }
  };

  handleEmploymentEducationClaimChange = (claimFieldKey) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom(this.getEmploymentEducationResetFieldsByClaim(claimFieldKey));
    }
    this.props.onFormDataChange(`form.${claimFieldKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleTravelCostsTransportEmploymentChange = (transportParentKey, transportResetFields) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom(transportResetFields);
    }
    this.props.onFormDataChange(`form.${transportParentKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleTransportSubOptionChangeEmployment = (fieldKey, itemsKey) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom([itemsKey]);
    }
    this.props.onFormDataChange(`form.${fieldKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleLegalIssuesClaimChange = (claimFieldKey) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom(this.getLegalIssuesResetFieldsByClaim(claimFieldKey));
    }
    this.props.onFormDataChange(`form.${claimFieldKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleTravelCostsTransportLegalIssuesChange = (transportParentKey, transportResetFields) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom(transportResetFields);
    }
    this.props.onFormDataChange(`form.${transportParentKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleTransportSubOptionChangeLegalIssues = (fieldKey, itemsKey) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom([itemsKey]);
    }
    this.props.onFormDataChange(`form.${fieldKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleCancellationsDelaysCarrierWeatherClaimChange = (claimFieldKey) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom(this.getCancellationsDelaysCarrierWeatherResetFieldsByClaim(claimFieldKey));
    }
    this.props.onFormDataChange(`form.${claimFieldKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleTravelCostsTransportCancellationsDelaysCarrierWeatherChange = (transportParentKey, transportResetFields) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom(transportResetFields);
    }
    this.props.onFormDataChange(`form.${transportParentKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleTransportSubOptionChangeCancellationsDelaysCarrierWeather = (fieldKey, itemsKey) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom([itemsKey]);
    }
    this.props.onFormDataChange(`form.${fieldKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleOtherReasonsClaimChange = (claimFieldKey) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (claimFieldKey === 'otherReasonsClaimNaturalDisaster' && !checked) {
      this.props.onResetFormCustom(this.OTHER_REASONS_NATURAL_DISASTER_RESET_FIELDS);
    }
    if (claimFieldKey === 'otherReasonsClaimUnforeseeableEvent' && !checked) {
      this.props.onResetFormCustom(this.OTHER_REASONS_UNFORESEEABLE_EVENT_RESET_FIELDS);
    }
    this.props.onFormDataChange(`form.${claimFieldKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleTravelCostsTransportOtherReasonsNaturalDisasterChange = (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom(this.OTHER_REASONS_NATURAL_DISASTER_TRANSPORT_RESET_FIELDS);
    }
    this.props.onFormDataChange('form.travelCostsTransportOtherReasonsNaturalDisaster', checked);
    this.props.setFormHasChanges(true);
  };

  handleTransportSubOptionChangeOtherReasonsNaturalDisaster = (fieldKey, itemsKey) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom([itemsKey]);
    }
    this.props.onFormDataChange(`form.${fieldKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleTravelCostsTransportOtherReasonsUnforeseeableEventChange = (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom(this.OTHER_REASONS_UNFORESEEABLE_EVENT_TRANSPORT_RESET_FIELDS);
    }
    this.props.onFormDataChange('form.travelCostsTransportOtherReasonsUnforeseeableEvent', checked);
    this.props.setFormHasChanges(true);
  };

  handleTransportSubOptionChangeOtherReasonsUnforeseeableEvent = (fieldKey, itemsKey) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom([itemsKey]);
    }
    this.props.onFormDataChange(`form.${fieldKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleHealthClaimChange = (propertyName) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (propertyName === 'form.healthClaimSelfOrCompanion' && !checked) {
      this.props.onResetFormCustom([
        ...this.TRAVEL_COSTS_TRANSPORT_RESET_FIELDS,
        ...this.HEALTH_SELF_COMPANION_OTHER_EXPENSE_RESET_FIELDS,
        ...this.DEATH_EXPENSE_RESET_FIELDS,
      ]);
    }
    if (propertyName === 'form.healthClaimFamilyMember' && !checked) {
      this.props.onResetFormCustom(this.FAMILY_MEMBER_RESET_FIELDS);
    }
    if (propertyName === 'form.healthClaimBusinessPartnerEtc' && !checked) {
      this.props.onResetFormCustom(this.BUSINESS_PARTNER_ETC_RESET_FIELDS);
    }
    if (propertyName === 'form.healthClaimHostAtDestination' && !checked) {
      this.props.onResetFormCustom(this.HOST_AT_DESTINATION_RESET_FIELDS);
    }
    this.props.onFormDataChange(propertyName, checked);
    this.props.setFormHasChanges(true);
  };

  handleTravelCostsTransportChange = (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom(this.TRAVEL_COSTS_TRANSPORT_RESET_FIELDS);
    }
    this.props.onFormDataChange('form.travelCostsTransport', checked);
    this.props.setFormHasChanges(true);
  };

  handleExpensesRelatedToDeathChange = (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom(this.DEATH_EXPENSE_RESET_FIELDS);
    }
    this.props.onFormDataChange('form.expensesRelatedToDeath', checked);
    this.props.setFormHasChanges(true);
  };

  handleTransportSubOptionChange = (fieldKey, itemsKey) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom([itemsKey]);
    }
    this.props.onFormDataChange(`form.${fieldKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  FAMILY_MEMBER_TRANSPORT_RESET_FIELDS = [
    'travelCostsNonRefundPrepaidAirfareFamilyMember',
    'travelCostsChangeFeesFamilyMember',
    'travelCostsAirfareReturnPointFamilyMember',
    'travelCostsSeatUpgradeFamilyMember',
    'travelCostsTransportCatchUpFamilyMember',
    'tripInterruptionFamilyMemberNonRefundPrepaidAirfareItems',
    'tripInterruptionFamilyMemberChangeFeesItems',
    'tripInterruptionFamilyMemberAirfareReturnPointItems',
    'tripInterruptionFamilyMemberSeatUpgradeItems',
    'tripInterruptionFamilyMemberTransportCatchUpItems',
  ];

  handleTravelCostsTransportFamilyMemberChange = (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom(this.FAMILY_MEMBER_TRANSPORT_RESET_FIELDS);
    }
    this.props.onFormDataChange('form.travelCostsTransportFamilyMember', checked);
    this.props.setFormHasChanges(true);
  };

  handleTransportSubOptionChangeFamilyMember = (fieldKey, itemsKey) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom([itemsKey]);
    }
    this.props.onFormDataChange(`form.${fieldKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleTravelCostsTransportBusinessPartnerEtcChange = (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom(this.BUSINESS_PARTNER_ETC_TRANSPORT_RESET_FIELDS);
    }
    this.props.onFormDataChange('form.travelCostsTransportBusinessPartnerEtc', checked);
    this.props.setFormHasChanges(true);
  };

  handleTransportSubOptionChangeBusinessPartnerEtc = (fieldKey, itemsKey) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom([itemsKey]);
    }
    this.props.onFormDataChange(`form.${fieldKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleTravelCostsTransportHostAtDestinationChange = (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom(this.HOST_AT_DESTINATION_TRANSPORT_RESET_FIELDS);
    }
    this.props.onFormDataChange('form.travelCostsTransportHostAtDestination', checked);
    this.props.setFormHasChanges(true);
  };

  handleTransportSubOptionChangeHostAtDestination = (fieldKey, itemsKey) => (event) => {
    const checked = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (!checked) {
      this.props.onResetFormCustom([itemsKey]);
    }
    this.props.onFormDataChange(`form.${fieldKey}`, checked);
    this.props.setFormHasChanges(true);
  };

  handleTransportExpenseItemsChange = (itemsKey, newData) => {
    this.props.onFormDataChange(`form.${itemsKey}`, newData);
    this.props.setFormHasChanges(true);
  };

  getFormValue = (form, key) => (form.get ? form.get(key) : form[key]);

  /** Normalized checkbox checked state: true or 'true' => checked, otherwise unchecked */
  isChecked = (form, key) => {
    const v = this.getFormValue(form, key);
    return v === true || v === 'true';
  };

  getExpensesSelected = (form) => {
    const expenseTypesClaiming = form.expenseTypesClaiming;
    return !!expenseTypesClaiming && typeof expenseTypesClaiming.toJS === 'function'
      ? expenseTypesClaiming.toJS()
      : expenseTypesClaiming || [];
  };

  renderPageDescription = () => (
    <div className="col-sm-12 form-group">
      <p>
        <strong>
          <FormattedMessage {...messages.pageDescriptionBold} />
        </strong>
      </p>
      <p>
        <FormattedMessage {...messages.pageDescriptionBody} />
      </p>
    </div>
  );

  getTravelCostsTransportTooltipHtml = () => {
    const { intl } = this.props;
    return `
      <div style="text-align: left; padding: 2px;">
        <span style="font-family: inherit;">${intl.formatMessage(messages.travelCostsTransportTooltip)}</span>
        <ul style="list-style-type: circle; padding-left: 20px; margin: 8px 0 0 0;">
          <li style="margin-bottom: 4px;">${intl.formatMessage(messages.travelCostsTransportTooltip1)}</li>
          <li style="margin-bottom: 4px;">${intl.formatMessage(messages.travelCostsTransportTooltip2)}</li>
          <li style="margin-bottom: 4px;">${intl.formatMessage(messages.travelCostsTransportTooltip3)}</li>
          <li style="margin-bottom: 4px;">${intl.formatMessage(messages.travelCostsTransportTooltip4)}</li>
          <li style="margin-bottom: 4px;">${intl.formatMessage(messages.travelCostsTransportTooltip5)}</li>
        </ul>
      </div>
    `;
  };

  renderHealthClaimCheckbox = (form, isReadOnly, fieldKey, message) => (
    <div key={fieldKey} className="col-sm-12 form-group">
      <input
        disabled={isReadOnly}
        type="checkbox"
        checked={this.isChecked(form, fieldKey)}
        onChange={this.handleHealthClaimChange(`form.${fieldKey}`)}
      />
      <span>
        <FormattedMessage {...message} />
      </span>
    </div>
  );

  renderHealthSelfCompanionExpenseOptions = (form, isReadOnly) => {
    const travelCostsTransport = this.isChecked(form, 'travelCostsTransport');
    return (
      <div className="col-sm-12 form-group ml-4 mt-2">
        <div className="col-sm-12 form-group mb-3">
          <div className="col-sm-12 form-group">
            <input
              disabled={isReadOnly}
              type="checkbox"
              checked={travelCostsTransport}
              onChange={this.handleTravelCostsTransportChange}
            />
            <span>
              <FormattedMessage {...messages.travelCostsTransport} />
            </span>
            <FNOLTooltip
              dataFor="travel-costs-transport-tooltip"
              html={true}
              content={this.getTravelCostsTransportTooltipHtml()}
            />
          </div>
          {travelCostsTransport && (
            <div className="col-sm-12 form-group ml-4 mt-2">
              {this.TRANSPORT_EXPENSE_CONFIGS.map(({ itemsKey, fieldKey, label, tooltip }) => {
                const checked = this.isChecked(form, fieldKey);
                const items = this.getFormValue(form, itemsKey);
                const itemsList = Array.isArray(items) ? items : [];
                return (
                  <div key={itemsKey} className="col-sm-12 form-group mb-3">
                    <div className="col-sm-12 form-group">
                      <input
                        disabled={isReadOnly}
                        type="checkbox"
                        checked={checked}
                        onChange={this.handleTransportSubOptionChange(fieldKey, itemsKey)}
                      />
                      <span>
                        <FormattedMessage {...label} />
                      </span>
                      {tooltip && (
                        <FNOLTooltip
                          dataFor={`${itemsKey}-tooltip`}
                          content={this.props.intl.formatMessage(tooltip)}
                        />
                      )}
                    </div>
                    {checked && (
                      <div className="col-sm-12 form-group ml-4 mt-2">
                        <HealthExpenseList
                          items={itemsList}
                          itemsKey={itemsKey}
                          onChange={this.handleTransportExpenseItemsChange}
                          disabled={isReadOnly}
                          validatorGroup={this.validatorGroup}
                          validationCounter={this.state.validationCounter}
                          insuredsList={this.props.insuredsList}
                          intl={this.props.intl}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {this.OTHER_HEALTH_EXPENSE_CONFIGS.map(({ itemsKey, fieldKey, label, tooltip }) => {
          const checked = this.isChecked(form, fieldKey);
          const items = this.getFormValue(form, itemsKey);
          const itemsList = Array.isArray(items) ? items : [];
          return (
            <div key={itemsKey} className="col-sm-12 form-group mb-3">
              <div className="col-sm-12 form-group">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  checked={checked}
                  onChange={this.handleTransportSubOptionChange(fieldKey, itemsKey)}
                />
                <span>
                  <FormattedMessage {...label} />
                </span>
                {tooltip && (
                  <FNOLTooltip
                    dataFor={`${itemsKey}-tooltip`}
                    content={this.props.intl.formatMessage(tooltip)}
                  />
                )}
              </div>
              {checked && (
                <div className="col-sm-12 form-group ml-4 mt-2">
                  <HealthExpenseList
                    items={itemsList}
                    itemsKey={itemsKey}
                    onChange={this.handleTransportExpenseItemsChange}
                    disabled={isReadOnly}
                    validatorGroup={this.validatorGroup}
                    validationCounter={this.state.validationCounter}
                    insuredsList={this.props.insuredsList}
                    intl={this.props.intl}
                  />
                </div>
              )}
            </div>
          );
        })}

        <div className="col-sm-12 form-group mb-3">
          <div className="col-sm-12 form-group">
            <input
              disabled={isReadOnly}
              type="checkbox"
              checked={this.isChecked(form, 'expensesRelatedToDeath')}
              onChange={this.handleExpensesRelatedToDeathChange}
            />
            <span>
              <FormattedMessage {...messages.expensesRelatedToDeath} />
            </span>
          </div>
          {this.isChecked(form, 'expensesRelatedToDeath') && (
            <div className="col-sm-12 form-group ml-4 mt-2">
              {this.DEATH_EXPENSE_CONFIGS.map(({ itemsKey, fieldKey, label, tooltip }) => {
                const checked = this.isChecked(form, fieldKey);
                const items = this.getFormValue(form, itemsKey);
                const itemsList = Array.isArray(items) ? items : [];
                return (
                  <div key={itemsKey} className="col-sm-12 form-group mb-3">
                    <div className="col-sm-12 form-group">
                      <input
                        disabled={isReadOnly}
                        type="checkbox"
                        checked={checked}
                        onChange={this.handleTransportSubOptionChange(fieldKey, itemsKey)}
                      />
                      <span>
                        <FormattedMessage {...label} />
                      </span>
                      {tooltip && (
                        <FNOLTooltip
                          dataFor={`${itemsKey}-tooltip`}
                          content={this.props.intl.formatMessage(tooltip)}
                        />
                      )}
                    </div>
                    {checked && (
                      <div className="col-sm-12 form-group ml-4 mt-2">
                        <HealthExpenseList
                          items={itemsList}
                          itemsKey={itemsKey}
                          onChange={this.handleTransportExpenseItemsChange}
                          disabled={isReadOnly}
                          validatorGroup={this.validatorGroup}
                          validationCounter={this.state.validationCounter}
                          insuredsList={this.props.insuredsList}
                          intl={this.props.intl}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  renderHealthFamilyMemberExpenseOptions = (form, isReadOnly) => {
    const travelCostsTransportFM = this.isChecked(form, 'travelCostsTransportFamilyMember');
    return (
      <div className="col-sm-12 form-group ml-4 mt-2">
        <div className="col-sm-12 form-group mb-3">
          <div className="col-sm-12 form-group">
            <input
              disabled={isReadOnly}
              type="checkbox"
              checked={travelCostsTransportFM}
              onChange={this.handleTravelCostsTransportFamilyMemberChange}
            />
            <span>
              <FormattedMessage {...messages.travelCostsTransport} />
            </span>
            <FNOLTooltip
              dataFor="travel-costs-transport-fm-tooltip"
              html={true}
              content={this.getTravelCostsTransportTooltipHtml()}
            />
          </div>
          {travelCostsTransportFM && (
            <div className="col-sm-12 form-group ml-4 mt-2">
              {this.TRANSPORT_EXPENSE_CONFIGS_FAMILY_MEMBER.map(({ itemsKey, fieldKey, label, tooltip }) => {
                const checked = this.isChecked(form, fieldKey);
                const items = this.getFormValue(form, itemsKey);
                const itemsList = Array.isArray(items) ? items : [];
                return (
                  <div key={itemsKey} className="col-sm-12 form-group mb-3">
                    <div className="col-sm-12 form-group">
                      <input
                        disabled={isReadOnly}
                        type="checkbox"
                        checked={checked}
                        onChange={this.handleTransportSubOptionChangeFamilyMember(fieldKey, itemsKey)}
                      />
                      <span>
                        <FormattedMessage {...label} />
                      </span>
                      {tooltip && (
                        <FNOLTooltip
                          dataFor={`${itemsKey}-tooltip`}
                          content={this.props.intl.formatMessage(tooltip)}
                        />
                      )}
                    </div>
                    {checked && (
                      <div className="col-sm-12 form-group ml-4 mt-2">
                        <HealthExpenseList
                          items={itemsList}
                          itemsKey={itemsKey}
                          onChange={this.handleTransportExpenseItemsChange}
                          disabled={isReadOnly}
                          validatorGroup={this.validatorGroup}
                          validationCounter={this.state.validationCounter}
                          insuredsList={this.props.insuredsList}
                          intl={this.props.intl}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {this.OTHER_HEALTH_EXPENSE_CONFIGS_FAMILY_MEMBER.map(({ itemsKey, fieldKey, label, tooltip }) => {
          const checked = this.isChecked(form, fieldKey);
          const items = this.getFormValue(form, itemsKey);
          const itemsList = Array.isArray(items) ? items : [];
          return (
            <div key={itemsKey} className="col-sm-12 form-group mb-3">
              <div className="col-sm-12 form-group">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  checked={checked}
                  onChange={this.handleTransportSubOptionChangeFamilyMember(fieldKey, itemsKey)}
                />
                <span>
                  <FormattedMessage {...label} />
                </span>
                {tooltip && (
                  <FNOLTooltip
                    dataFor={`${itemsKey}-tooltip`}
                    content={this.props.intl.formatMessage(tooltip)}
                  />
                )}
              </div>
              {checked && (
                <div className="col-sm-12 form-group ml-4 mt-2">
                  <HealthExpenseList
                    items={itemsList}
                    itemsKey={itemsKey}
                    onChange={this.handleTransportExpenseItemsChange}
                    disabled={isReadOnly}
                    validatorGroup={this.validatorGroup}
                    validationCounter={this.state.validationCounter}
                    insuredsList={this.props.insuredsList}
                    intl={this.props.intl}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  renderHealthBusinessPartnerEtcExpenseOptions = (form, isReadOnly) => {
    const travelCostsTransportBPE = this.isChecked(form, 'travelCostsTransportBusinessPartnerEtc');
    return (
      <div className="col-sm-12 form-group ml-4 mt-2">
        <div className="col-sm-12 form-group mb-3">
          <div className="col-sm-12 form-group">
            <input
              disabled={isReadOnly}
              type="checkbox"
              checked={travelCostsTransportBPE}
              onChange={this.handleTravelCostsTransportBusinessPartnerEtcChange}
            />
            <span>
              <FormattedMessage {...messages.travelCostsTransport} />
            </span>
            <FNOLTooltip
              dataFor="travel-costs-transport-bpe-tooltip"
              html={true}
              content={this.getTravelCostsTransportTooltipHtml()}
            />
          </div>
          {travelCostsTransportBPE && (
            <div className="col-sm-12 form-group ml-4 mt-2">
              {this.TRANSPORT_EXPENSE_CONFIGS_BUSINESS_PARTNER_ETC.map(({ itemsKey, fieldKey, label, tooltip }) => {
                const checked = this.isChecked(form, fieldKey);
                const items = this.getFormValue(form, itemsKey);
                const itemsList = Array.isArray(items) ? items : [];
                return (
                  <div key={itemsKey} className="col-sm-12 form-group mb-3">
                    <div className="col-sm-12 form-group">
                      <input
                        disabled={isReadOnly}
                        type="checkbox"
                        checked={checked}
                        onChange={this.handleTransportSubOptionChangeBusinessPartnerEtc(fieldKey, itemsKey)}
                      />
                      <span>
                        <FormattedMessage {...label} />
                      </span>
                      {tooltip && (
                        <FNOLTooltip
                          dataFor={`${itemsKey}-tooltip`}
                          content={this.props.intl.formatMessage(tooltip)}
                        />
                      )}
                    </div>
                    {checked && (
                      <div className="col-sm-12 form-group ml-4 mt-2">
                        <HealthExpenseList
                          items={itemsList}
                          itemsKey={itemsKey}
                          onChange={this.handleTransportExpenseItemsChange}
                          disabled={isReadOnly}
                          validatorGroup={this.validatorGroup}
                          validationCounter={this.state.validationCounter}
                          insuredsList={this.props.insuredsList}
                          intl={this.props.intl}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {this.OTHER_HEALTH_EXPENSE_CONFIGS_BUSINESS_PARTNER_ETC.map(({ itemsKey, fieldKey, label, tooltip }) => {
          const checked = this.isChecked(form, fieldKey);
          const items = this.getFormValue(form, itemsKey);
          const itemsList = Array.isArray(items) ? items : [];
          return (
            <div key={itemsKey} className="col-sm-12 form-group mb-3">
              <div className="col-sm-12 form-group">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  checked={checked}
                  onChange={this.handleTransportSubOptionChangeBusinessPartnerEtc(fieldKey, itemsKey)}
                />
                <span>
                  <FormattedMessage {...label} />
                </span>
                {tooltip && (
                  <FNOLTooltip
                    dataFor={`${itemsKey}-tooltip`}
                    content={this.props.intl.formatMessage(tooltip)}
                  />
                )}
              </div>
              {checked && (
                <div className="col-sm-12 form-group ml-4 mt-2">
                  <HealthExpenseList
                    items={itemsList}
                    itemsKey={itemsKey}
                    onChange={this.handleTransportExpenseItemsChange}
                    disabled={isReadOnly}
                    validatorGroup={this.validatorGroup}
                    validationCounter={this.state.validationCounter}
                    insuredsList={this.props.insuredsList}
                    intl={this.props.intl}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  renderHealthHostAtDestinationExpenseOptions = (form, isReadOnly) => {
    const travelCostsTransportHAD = this.isChecked(form, 'travelCostsTransportHostAtDestination');
    return (
      <div className="col-sm-12 form-group ml-4 mt-2">
        <div className="col-sm-12 form-group mb-3">
          <div className="col-sm-12 form-group">
            <input
              disabled={isReadOnly}
              type="checkbox"
              checked={travelCostsTransportHAD}
              onChange={this.handleTravelCostsTransportHostAtDestinationChange}
            />
            <span>
              <FormattedMessage {...messages.travelCostsTransport} />
            </span>
            <FNOLTooltip
              dataFor="travel-costs-transport-had-tooltip"
              html={true}
              content={this.getTravelCostsTransportTooltipHtml()}
            />
          </div>
          {travelCostsTransportHAD && (
            <div className="col-sm-12 form-group ml-4 mt-2">
              {this.TRANSPORT_EXPENSE_CONFIGS_HOST_AT_DESTINATION.map(({ itemsKey, fieldKey, label, tooltip }) => {
                const checked = this.isChecked(form, fieldKey);
                const items = this.getFormValue(form, itemsKey);
                const itemsList = Array.isArray(items) ? items : [];
                return (
                  <div key={itemsKey} className="col-sm-12 form-group mb-3">
                    <div className="col-sm-12 form-group">
                      <input
                        disabled={isReadOnly}
                        type="checkbox"
                        checked={checked}
                        onChange={this.handleTransportSubOptionChangeHostAtDestination(fieldKey, itemsKey)}
                      />
                      <span>
                        <FormattedMessage {...label} />
                      </span>
                      {tooltip && (
                        <FNOLTooltip
                          dataFor={`${itemsKey}-tooltip`}
                          content={this.props.intl.formatMessage(tooltip)}
                        />
                      )}
                    </div>
                    {checked && (
                      <div className="col-sm-12 form-group ml-4 mt-2">
                        <HealthExpenseList
                          items={itemsList}
                          itemsKey={itemsKey}
                          onChange={this.handleTransportExpenseItemsChange}
                          disabled={isReadOnly}
                          validatorGroup={this.validatorGroup}
                          validationCounter={this.state.validationCounter}
                          insuredsList={this.props.insuredsList}
                          intl={this.props.intl}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {this.OTHER_HEALTH_EXPENSE_CONFIGS_HOST_AT_DESTINATION.map(({ itemsKey, fieldKey, label, tooltip }) => {
          const checked = this.isChecked(form, fieldKey);
          const items = this.getFormValue(form, itemsKey);
          const itemsList = Array.isArray(items) ? items : [];
          return (
            <div key={itemsKey} className="col-sm-12 form-group mb-3">
              <div className="col-sm-12 form-group">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  checked={checked}
                  onChange={this.handleTransportSubOptionChangeHostAtDestination(fieldKey, itemsKey)}
                />
                <span>
                  <FormattedMessage {...label} />
                </span>
                {tooltip && (
                  <FNOLTooltip
                    dataFor={`${itemsKey}-tooltip`}
                    content={this.props.intl.formatMessage(tooltip)}
                  />
                )}
              </div>
              {checked && (
                <div className="col-sm-12 form-group ml-4 mt-2">
                  <HealthExpenseList
                    items={itemsList}
                    itemsKey={itemsKey}
                    onChange={this.handleTransportExpenseItemsChange}
                    disabled={isReadOnly}
                    validatorGroup={this.validatorGroup}
                    validationCounter={this.state.validationCounter}
                    insuredsList={this.props.insuredsList}
                    intl={this.props.intl}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  renderHealthFollowUpCheckboxes = (form, isReadOnly) => (
    <div className="col-sm-12 form-group ml-4 mt-2">
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          checked={this.isChecked(form, 'healthClaimSelfOrCompanion')}
          onChange={this.handleHealthClaimChange('form.healthClaimSelfOrCompanion')}
        />
        <span>
          <FormattedMessage {...messages.healthClaimSelfOrCompanion} />
        </span>
      </div>
      {this.isChecked(form, 'healthClaimSelfOrCompanion') && this.renderHealthSelfCompanionExpenseOptions(form, isReadOnly)}
      {this.renderHealthClaimCheckbox(form, isReadOnly, 'healthClaimFamilyMember', messages.healthClaimFamilyMember)}
      {this.isChecked(form, 'healthClaimFamilyMember') && this.renderHealthFamilyMemberExpenseOptions(form, isReadOnly)}
      {this.renderHealthClaimCheckbox(form, isReadOnly, 'healthClaimBusinessPartnerEtc', messages.healthClaimBusinessPartnerEtc)}
      {this.isChecked(form, 'healthClaimBusinessPartnerEtc') && this.renderHealthBusinessPartnerEtcExpenseOptions(form, isReadOnly)}
      {this.renderHealthClaimCheckbox(form, isReadOnly, 'healthClaimHostAtDestination', messages.healthClaimHostAtDestination)}
      {this.isChecked(form, 'healthClaimHostAtDestination') && this.renderHealthHostAtDestinationExpenseOptions(form, isReadOnly)}
    </div>
  );

  renderPregnancyAndAdoptionExpenseOptions = (form, isReadOnly) => {
    const travelCostsTransportPregnancyAdoption = this.isChecked(form, 'travelCostsTransportPregnancyAdoption');
    return (
      <div className="col-sm-12 form-group ml-4 mt-2">
        <div className="col-sm-12 form-group mb-3">
          <div className="col-sm-12 form-group">
            <input
              disabled={isReadOnly}
              type="checkbox"
              checked={travelCostsTransportPregnancyAdoption}
              onChange={this.handleTravelCostsTransportPregnancyAdoptionChange}
            />
            <span>
              <FormattedMessage {...messages.travelCostsTransport} />
            </span>
            <FNOLTooltip
              dataFor="travel-costs-transport-pa-tooltip"
              html={true}
              content={this.getTravelCostsTransportTooltipHtml()}
            />
          </div>
          {travelCostsTransportPregnancyAdoption && (
            <div className="col-sm-12 form-group ml-4 mt-2">
              {this.PREGNANCY_ADOPTION_TRANSPORT_EXPENSE_CONFIGS.map(({ itemsKey, fieldKey, label, tooltip }) => {
                const checked = this.isChecked(form, fieldKey);
                const items = this.getFormValue(form, itemsKey);
                const itemsList = Array.isArray(items) ? items : [];
                return (
                  <div key={itemsKey} className="col-sm-12 form-group mb-3">
                    <div className="col-sm-12 form-group">
                      <input
                        disabled={isReadOnly}
                        type="checkbox"
                        checked={checked}
                        onChange={this.handleTransportSubOptionChangePregnancyAdoption(fieldKey, itemsKey)}
                      />
                      <span>
                        <FormattedMessage {...label} />
                      </span>
                      {tooltip && (
                        <FNOLTooltip
                          dataFor={`${itemsKey}-tooltip`}
                          content={this.props.intl.formatMessage(tooltip)}
                        />
                      )}
                    </div>
                    {checked && (
                      <div className="col-sm-12 form-group ml-4 mt-2">
                        <HealthExpenseList
                          items={itemsList}
                          itemsKey={itemsKey}
                          onChange={this.handleTransportExpenseItemsChange}
                          disabled={isReadOnly}
                          validatorGroup={this.validatorGroup}
                          validationCounter={this.state.validationCounter}
                          insuredsList={this.props.insuredsList}
                          intl={this.props.intl}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {this.PREGNANCY_ADOPTION_OTHER_EXPENSE_CONFIGS.map(({ itemsKey, fieldKey, label, tooltip }) => {
          const checked = this.isChecked(form, fieldKey);
          const items = this.getFormValue(form, itemsKey);
          const itemsList = Array.isArray(items) ? items : [];
          return (
            <div key={itemsKey} className="col-sm-12 form-group mb-3">
              <div className="col-sm-12 form-group">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  checked={checked}
                  onChange={this.handleTransportSubOptionChangePregnancyAdoption(fieldKey, itemsKey)}
                />
                <span>
                  <FormattedMessage {...label} />
                </span>
                {tooltip && (
                  <FNOLTooltip
                    dataFor={`${itemsKey}-tooltip`}
                    content={this.props.intl.formatMessage(tooltip)}
                  />
                )}
              </div>
              {checked && (
                <div className="col-sm-12 form-group ml-4 mt-2">
                  <HealthExpenseList
                    items={itemsList}
                    itemsKey={itemsKey}
                    onChange={this.handleTransportExpenseItemsChange}
                    disabled={isReadOnly}
                    validatorGroup={this.validatorGroup}
                    validationCounter={this.state.validationCounter}
                    insuredsList={this.props.insuredsList}
                    intl={this.props.intl}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  renderPregnancyAndAdoptionFollowUpCheckboxes = (form, isReadOnly) => (
    <div className="col-sm-12 form-group ml-4 mt-2">
      <div className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          checked={this.isChecked(form, 'pregnancyAdoptionClaimLegalAdoption')}
          onChange={this.handlePregnancyAdoptionClaimChange('form.pregnancyAdoptionClaimLegalAdoption')}
        />
        <span>
          <FormattedMessage {...messages.pregnancyAdoptionClaimLegalAdoption} />
        </span>
      </div>
      {this.isChecked(form, 'pregnancyAdoptionClaimLegalAdoption') && this.renderPregnancyAndAdoptionExpenseOptions(form, isReadOnly)}
    </div>
  );

  renderEmploymentEducationExpenseOptions = (form, isReadOnly, transportParentKey, transportResetFields, transportConfigs, otherConfigs) => {
    const travelCostsTransport = this.isChecked(form, transportParentKey);
    return (
      <div className="col-sm-12 form-group ml-4 mt-2">
        <div className="col-sm-12 form-group mb-3">
          <div className="col-sm-12 form-group">
            <input
              disabled={isReadOnly}
              type="checkbox"
              checked={travelCostsTransport}
              onChange={this.handleTravelCostsTransportEmploymentChange(transportParentKey, transportResetFields)}
            />
            <span>
              <FormattedMessage {...messages.travelCostsTransport} />
            </span>
            <FNOLTooltip
              dataFor={`${transportParentKey}-tooltip`}
              html={true}
              content={this.getTravelCostsTransportTooltipHtml()}
            />
          </div>
          {travelCostsTransport && (
            <div className="col-sm-12 form-group ml-4 mt-2">
              {transportConfigs.map(({ itemsKey, fieldKey, label, tooltip }) => {
                const checked = this.isChecked(form, fieldKey);
                const items = this.getFormValue(form, itemsKey);
                const itemsList = Array.isArray(items) ? items : [];
                return (
                  <div key={itemsKey} className="col-sm-12 form-group mb-3">
                    <div className="col-sm-12 form-group">
                      <input
                        disabled={isReadOnly}
                        type="checkbox"
                        checked={checked}
                        onChange={this.handleTransportSubOptionChangeEmployment(fieldKey, itemsKey)}
                      />
                      <span>
                        <FormattedMessage {...label} />
                      </span>
                      {tooltip && (
                        <FNOLTooltip dataFor={`${itemsKey}-tooltip`} content={this.props.intl.formatMessage(tooltip)} />
                      )}
                    </div>
                    {checked && (
                      <div className="col-sm-12 form-group ml-4 mt-2">
                        <HealthExpenseList
                          items={itemsList}
                          itemsKey={itemsKey}
                          onChange={this.handleTransportExpenseItemsChange}
                          disabled={isReadOnly}
                          validatorGroup={this.validatorGroup}
                          validationCounter={this.state.validationCounter}
                          insuredsList={this.props.insuredsList}
                          intl={this.props.intl}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {otherConfigs.map(({ itemsKey, fieldKey, label, tooltip }) => {
          const checked = this.isChecked(form, fieldKey);
          const items = this.getFormValue(form, itemsKey);
          const itemsList = Array.isArray(items) ? items : [];
          return (
            <div key={itemsKey} className="col-sm-12 form-group mb-3">
              <div className="col-sm-12 form-group">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  checked={checked}
                  onChange={this.handleTransportSubOptionChangeEmployment(fieldKey, itemsKey)}
                />
                <span>
                  <FormattedMessage {...label} />
                </span>
                {tooltip && (
                  <FNOLTooltip dataFor={`${itemsKey}-tooltip`} content={this.props.intl.formatMessage(tooltip)} />
                )}
              </div>
              {checked && (
                <div className="col-sm-12 form-group ml-4 mt-2">
                  <HealthExpenseList
                    items={itemsList}
                    itemsKey={itemsKey}
                    onChange={this.handleTransportExpenseItemsChange}
                    disabled={isReadOnly}
                    validatorGroup={this.validatorGroup}
                    validationCounter={this.state.validationCounter}
                    insuredsList={this.props.insuredsList}
                    intl={this.props.intl}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  renderEmploymentEducationClaimCheckbox = (form, isReadOnly, fieldKey, message) => (
    <div key={fieldKey} className="col-sm-12 form-group">
      <input
        disabled={isReadOnly}
        type="checkbox"
        checked={this.isChecked(form, fieldKey)}
        onChange={this.handleEmploymentEducationClaimChange(fieldKey)}
      />
      <span>
        <FormattedMessage {...message} />
      </span>
    </div>
  );

  renderLegalIssuesClaimCheckbox = (form, isReadOnly, fieldKey, message) => (
    <div key={fieldKey} className="col-sm-12 form-group">
      <input
        disabled={isReadOnly}
        type="checkbox"
        checked={this.isChecked(form, fieldKey)}
        onChange={this.handleLegalIssuesClaimChange(fieldKey)}
      />
      <span>
        <FormattedMessage {...message} />
      </span>
    </div>
  );

  renderEmploymentEducationFollowUpCheckboxes = (form, isReadOnly) => (
    <div className="col-sm-12 form-group ml-4 mt-2">
      {this.renderEmploymentEducationClaimCheckbox(form, isReadOnly, 'employmentEducationClaimJobTransfer', messages.employmentEducationClaimJobTransfer)}
      {this.isChecked(form, 'employmentEducationClaimJobTransfer') && this.renderEmploymentEducationExpenseOptions(form, isReadOnly, 'travelCostsTransportEmploymentJobTransfer', this.EMPLOYMENT_JOB_TRANSFER_TRANSPORT_RESET_FIELDS, this.EMPLOYMENT_JOB_TRANSFER_TRANSPORT_EXPENSE_CONFIGS, this.EMPLOYMENT_JOB_TRANSFER_OTHER_EXPENSE_CONFIGS)}
      {this.renderEmploymentEducationClaimCheckbox(form, isReadOnly, 'employmentEducationClaimInvoluntaryLossEmployment', messages.employmentEducationClaimInvoluntaryLossEmployment)}
      {this.isChecked(form, 'employmentEducationClaimInvoluntaryLossEmployment') && this.renderEmploymentEducationExpenseOptions(form, isReadOnly, 'travelCostsTransportEmploymentInvoluntaryLoss', this.EMPLOYMENT_INVOLUNTARY_LOSS_TRANSPORT_RESET_FIELDS, this.EMPLOYMENT_INVOLUNTARY_LOSS_TRANSPORT_EXPENSE_CONFIGS, this.EMPLOYMENT_INVOLUNTARY_LOSS_OTHER_EXPENSE_CONFIGS)}
      {this.renderEmploymentEducationClaimCheckbox(form, isReadOnly, 'employmentEducationClaimBusinessMeetingCancellation', messages.employmentEducationClaimBusinessMeetingCancellation)}
      {this.isChecked(form, 'employmentEducationClaimBusinessMeetingCancellation') && this.renderEmploymentEducationExpenseOptions(form, isReadOnly, 'travelCostsTransportEmploymentBusinessMeeting', this.EMPLOYMENT_BUSINESS_MEETING_TRANSPORT_RESET_FIELDS, this.EMPLOYMENT_BUSINESS_MEETING_TRANSPORT_EXPENSE_CONFIGS, this.EMPLOYMENT_BUSINESS_MEETING_OTHER_EXPENSE_CONFIGS)}
      {this.renderEmploymentEducationClaimCheckbox(form, isReadOnly, 'employmentEducationClaimConferenceCancellation', messages.employmentEducationClaimConferenceCancellation)}
      {this.isChecked(form, 'employmentEducationClaimConferenceCancellation') && this.renderEmploymentEducationExpenseOptions(form, isReadOnly, 'travelCostsTransportEmploymentConference', this.EMPLOYMENT_CONFERENCE_TRANSPORT_RESET_FIELDS, this.EMPLOYMENT_CONFERENCE_TRANSPORT_EXPENSE_CONFIGS, this.EMPLOYMENT_CONFERENCE_OTHER_EXPENSE_CONFIGS)}
    </div>
  );

  renderLegalIssuesExpenseOptions = (form, isReadOnly, transportParentKey, transportResetFields, transportConfigs, otherConfigs) => {
    const travelCostsTransport = this.isChecked(form, transportParentKey);
    return (
      <div className="col-sm-12 form-group ml-4 mt-2">
        <div className="col-sm-12 form-group mb-3">
          <div className="col-sm-12 form-group">
            <input
              disabled={isReadOnly}
              type="checkbox"
              checked={travelCostsTransport}
              onChange={this.handleTravelCostsTransportLegalIssuesChange(transportParentKey, transportResetFields)}
            />
            <span>
              <FormattedMessage {...messages.travelCostsTransport} />
            </span>
            <FNOLTooltip
              dataFor={`${transportParentKey}-tooltip`}
              html={true}
              content={this.getTravelCostsTransportTooltipHtml()}
            />
          </div>
          {travelCostsTransport && (
            <div className="col-sm-12 form-group ml-4 mt-2">
              {transportConfigs.map(({ itemsKey, fieldKey, label, tooltip }) => {
                const checked = this.isChecked(form, fieldKey);
                const items = this.getFormValue(form, itemsKey);
                const itemsList = Array.isArray(items) ? items : [];
                return (
                  <div key={itemsKey} className="col-sm-12 form-group mb-3">
                    <div className="col-sm-12 form-group">
                      <input
                        disabled={isReadOnly}
                        type="checkbox"
                        checked={checked}
                        onChange={this.handleTransportSubOptionChangeLegalIssues(fieldKey, itemsKey)}
                      />
                      <span>
                        <FormattedMessage {...label} />
                      </span>
                      {tooltip && (
                        <FNOLTooltip dataFor={`${itemsKey}-tooltip`} content={this.props.intl.formatMessage(tooltip)} />
                      )}
                    </div>
                    {checked && (
                      <div className="col-sm-12 form-group ml-4 mt-2">
                        <HealthExpenseList
                          items={itemsList}
                          itemsKey={itemsKey}
                          onChange={this.handleTransportExpenseItemsChange}
                          disabled={isReadOnly}
                          validatorGroup={this.validatorGroup}
                          validationCounter={this.state.validationCounter}
                          insuredsList={this.props.insuredsList}
                          intl={this.props.intl}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {otherConfigs.map(({ itemsKey, fieldKey, label, tooltip }) => {
          const checked = this.isChecked(form, fieldKey);
          const items = this.getFormValue(form, itemsKey);
          const itemsList = Array.isArray(items) ? items : [];
          return (
            <div key={itemsKey} className="col-sm-12 form-group mb-3">
              <div className="col-sm-12 form-group">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  checked={checked}
                  onChange={this.handleTransportSubOptionChangeLegalIssues(fieldKey, itemsKey)}
                />
                <span>
                  <FormattedMessage {...label} />
                </span>
                {tooltip && (
                  <FNOLTooltip dataFor={`${itemsKey}-tooltip`} content={this.props.intl.formatMessage(tooltip)} />
                )}
              </div>
              {checked && (
                <div className="col-sm-12 form-group ml-4 mt-2">
                  <HealthExpenseList
                    items={itemsList}
                    itemsKey={itemsKey}
                    onChange={this.handleTransportExpenseItemsChange}
                    disabled={isReadOnly}
                    validatorGroup={this.validatorGroup}
                    validationCounter={this.state.validationCounter}
                    insuredsList={this.props.insuredsList}
                    intl={this.props.intl}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  renderLegalIssuesFollowUpCheckboxes = (form, isReadOnly) => (
    <div className="col-sm-12 form-group ml-4 mt-2">
      {this.renderLegalIssuesClaimCheckbox(form, isReadOnly, 'legalIssuesClaimJuryDuty', messages.legalIssuesClaimJuryDuty)}
      {this.isChecked(form, 'legalIssuesClaimJuryDuty') && this.renderLegalIssuesExpenseOptions(form, isReadOnly, 'travelCostsTransportLegalIssuesJuryDuty', this.LEGAL_ISSUES_JURY_DUTY_TRANSPORT_RESET_FIELDS, this.LEGAL_ISSUES_JURY_DUTY_TRANSPORT_EXPENSE_CONFIGS, this.LEGAL_ISSUES_JURY_DUTY_OTHER_EXPENSE_CONFIGS)}
      {this.renderLegalIssuesClaimCheckbox(form, isReadOnly, 'legalIssuesClaimMilitaryService', messages.legalIssuesClaimMilitaryService)}
      {this.isChecked(form, 'legalIssuesClaimMilitaryService') && this.renderLegalIssuesExpenseOptions(form, isReadOnly, 'travelCostsTransportLegalIssuesMilitaryService', this.LEGAL_ISSUES_MILITARY_SERVICE_TRANSPORT_RESET_FIELDS, this.LEGAL_ISSUES_MILITARY_SERVICE_TRANSPORT_EXPENSE_CONFIGS, this.LEGAL_ISSUES_MILITARY_SERVICE_OTHER_EXPENSE_CONFIGS)}
      {this.renderLegalIssuesClaimCheckbox(form, isReadOnly, 'legalIssuesClaimTravelAdvisory', messages.legalIssuesClaimTravelAdvisory)}
      {this.isChecked(form, 'legalIssuesClaimTravelAdvisory') && this.renderLegalIssuesExpenseOptions(form, isReadOnly, 'travelCostsTransportLegalIssuesTravelAdvisory', this.LEGAL_ISSUES_TRAVEL_ADVISORY_TRANSPORT_RESET_FIELDS, this.LEGAL_ISSUES_TRAVEL_ADVISORY_TRANSPORT_EXPENSE_CONFIGS, this.LEGAL_ISSUES_TRAVEL_ADVISORY_OTHER_EXPENSE_CONFIGS)}
    </div>
  );

  renderCancellationsDelaysCarrierWeatherClaimCheckbox = (form, isReadOnly, fieldKey, message) => (
    <div key={fieldKey} className="col-sm-12 form-group">
      <input
        disabled={isReadOnly}
        type="checkbox"
        checked={this.isChecked(form, fieldKey)}
        onChange={this.handleCancellationsDelaysCarrierWeatherClaimChange(fieldKey)}
      />
      <span>
        <FormattedMessage {...message} />
      </span>
    </div>
  );

  renderCancellationsDelaysCarrierWeatherExpenseOptions = (form, isReadOnly, transportParentKey, transportResetFields, transportConfigs, otherConfigs) => {
    const travelCostsTransport = this.isChecked(form, transportParentKey);
    return (
      <div className="col-sm-12 form-group ml-4 mt-2">
        <div className="col-sm-12 form-group mb-3">
          <div className="col-sm-12 form-group">
            <input
              disabled={isReadOnly}
              type="checkbox"
              checked={travelCostsTransport}
              onChange={this.handleTravelCostsTransportCancellationsDelaysCarrierWeatherChange(transportParentKey, transportResetFields)}
            />
            <span>
              <FormattedMessage {...messages.travelCostsTransport} />
            </span>
            <FNOLTooltip
              dataFor={`${transportParentKey}-tooltip`}
              html={true}
              content={this.getTravelCostsTransportTooltipHtml()}
            />
          </div>
          {travelCostsTransport && (
            <div className="col-sm-12 form-group ml-4 mt-2">
              {transportConfigs.map(({ itemsKey, fieldKey, label, tooltip }) => {
                const checked = this.isChecked(form, fieldKey);
                const items = this.getFormValue(form, itemsKey);
                const itemsList = Array.isArray(items) ? items : [];
                return (
                  <div key={itemsKey} className="col-sm-12 form-group mb-3">
                    <div className="col-sm-12 form-group">
                      <input
                        disabled={isReadOnly}
                        type="checkbox"
                        checked={checked}
                        onChange={this.handleTransportSubOptionChangeCancellationsDelaysCarrierWeather(fieldKey, itemsKey)}
                      />
                      <span>
                        <FormattedMessage {...label} />
                      </span>
                      {tooltip && (
                        <FNOLTooltip
                          dataFor={`${itemsKey}-tooltip`}
                          content={this.props.intl.formatMessage(tooltip)}
                        />
                      )}
                    </div>
                    {checked && (
                      <div className="col-sm-12 form-group ml-4 mt-2">
                        <HealthExpenseList
                          items={itemsList}
                          itemsKey={itemsKey}
                          onChange={this.handleTransportExpenseItemsChange}
                          disabled={isReadOnly}
                          validatorGroup={this.validatorGroup}
                          validationCounter={this.state.validationCounter}
                          insuredsList={this.props.insuredsList}
                          intl={this.props.intl}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {otherConfigs.map(({ itemsKey, fieldKey, label, tooltip }) => {
          const checked = this.isChecked(form, fieldKey);
          const items = this.getFormValue(form, itemsKey);
          const itemsList = Array.isArray(items) ? items : [];
          return (
            <div key={itemsKey} className="col-sm-12 form-group mb-3">
              <div className="col-sm-12 form-group">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  checked={checked}
                  onChange={this.handleTransportSubOptionChangeCancellationsDelaysCarrierWeather(fieldKey, itemsKey)}
                />
                <span>
                  <FormattedMessage {...label} />
                </span>
                {tooltip && (
                  <FNOLTooltip
                    dataFor={`${itemsKey}-tooltip`}
                    content={this.props.intl.formatMessage(tooltip)}
                  />
                )}
              </div>
              {checked && (
                <div className="col-sm-12 form-group ml-4 mt-2">
                  <HealthExpenseList
                    items={itemsList}
                    itemsKey={itemsKey}
                    onChange={this.handleTransportExpenseItemsChange}
                    disabled={isReadOnly}
                    validatorGroup={this.validatorGroup}
                    validationCounter={this.state.validationCounter}
                    insuredsList={this.props.insuredsList}
                    intl={this.props.intl}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  renderCancellationsDelaysCarrierWeatherTourOperatorCancellationExpenseOptions = (form, isReadOnly, expenseConfigs) => {
    return (
      <div className="col-sm-12 form-group ml-4 mt-2">
        {expenseConfigs.map(({ itemsKey, fieldKey, label, tooltip }) => {
          const checked = this.isChecked(form, fieldKey);
          const items = this.getFormValue(form, itemsKey);
          const itemsList = Array.isArray(items) ? items : [];
          return (
            <div key={itemsKey} className="col-sm-12 form-group mb-3">
              <div className="col-sm-12 form-group">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  checked={checked}
                  onChange={this.handleTransportSubOptionChangeCancellationsDelaysCarrierWeather(fieldKey, itemsKey)}
                />
                <span>
                  <FormattedMessage {...label} />
                </span>
                {tooltip && (
                  <FNOLTooltip
                    dataFor={`${itemsKey}-tooltip`}
                    content={this.props.intl.formatMessage(tooltip)}
                  />
                )}
              </div>
              {checked && (
                <div className="col-sm-12 form-group ml-4 mt-2">
                  <HealthExpenseList
                    items={itemsList}
                    itemsKey={itemsKey}
                    onChange={this.handleTransportExpenseItemsChange}
                    disabled={isReadOnly}
                    validatorGroup={this.validatorGroup}
                    validationCounter={this.state.validationCounter}
                    insuredsList={this.props.insuredsList}
                    intl={this.props.intl}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  renderCancellationsDelaysCarrierWeatherFollowUpCheckboxes = (form, isReadOnly) => (
    <div className="col-sm-12 form-group ml-4 mt-2">
      {this.renderCancellationsDelaysCarrierWeatherClaimCheckbox(form, isReadOnly, 'cancellationsDelaysCarrierWeatherClaimMissedConnection', messages.cancellationsDelaysCarrierWeatherClaimMissedConnection)}
      {this.isChecked(form, 'cancellationsDelaysCarrierWeatherClaimMissedConnection') && this.renderCancellationsDelaysCarrierWeatherExpenseOptions(form, isReadOnly, 'travelCostsTransportCancellationsDelaysCarrierWeatherMissedConnection', this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_MISSED_CONNECTION_TRANSPORT_RESET_FIELDS, this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_MISSED_CONNECTION_TRANSPORT_EXPENSE_CONFIGS, this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_MISSED_CONNECTION_OTHER_EXPENSE_CONFIGS)}
      {this.renderCancellationsDelaysCarrierWeatherClaimCheckbox(form, isReadOnly, 'cancellationsDelaysCarrierWeatherClaimFourHoursNoConnection', messages.cancellationsDelaysCarrierWeatherClaimFourHoursNoConnection)}
      {this.isChecked(form, 'cancellationsDelaysCarrierWeatherClaimFourHoursNoConnection') && this.renderCancellationsDelaysCarrierWeatherExpenseOptions(form, isReadOnly, 'travelCostsTransportCancellationsDelaysCarrierWeatherFourHoursNoConnection', this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_FOUR_HOURS_NO_CONNECTION_TRANSPORT_RESET_FIELDS, this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_FOUR_HOURS_NO_CONNECTION_TRANSPORT_EXPENSE_CONFIGS, this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_FOUR_HOURS_NO_CONNECTION_OTHER_EXPENSE_CONFIGS)}
      {this.renderCancellationsDelaysCarrierWeatherClaimCheckbox(form, isReadOnly, 'cancellationsDelaysCarrierWeatherClaimCancellationOfCommonCarrier', messages.cancellationsDelaysCarrierWeatherClaimCancellationOfCommonCarrier)}
      {this.isChecked(form, 'cancellationsDelaysCarrierWeatherClaimCancellationOfCommonCarrier') && this.renderCancellationsDelaysCarrierWeatherExpenseOptions(form, isReadOnly, 'travelCostsTransportCancellationsDelaysCarrierWeatherCancellationOfCommonCarrier', this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_CANCELLATION_OF_COMMON_CARRIER_TRANSPORT_RESET_FIELDS, this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_CANCELLATION_OF_COMMON_CARRIER_TRANSPORT_EXPENSE_CONFIGS, this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_CANCELLATION_OF_COMMON_CARRIER_OTHER_EXPENSE_CONFIGS)}
      {this.renderCancellationsDelaysCarrierWeatherClaimCheckbox(form, isReadOnly, 'cancellationsDelaysCarrierWeatherClaimTourOperatorCancellation', messages.cancellationsDelaysCarrierWeatherClaimTourOperatorCancellation)}
      {this.isChecked(form, 'cancellationsDelaysCarrierWeatherClaimTourOperatorCancellation') && this.renderCancellationsDelaysCarrierWeatherTourOperatorCancellationExpenseOptions(form, isReadOnly, this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_TOUR_OPERATOR_CANCELLATION_EXPENSE_CONFIGS)}
      {this.renderCancellationsDelaysCarrierWeatherClaimCheckbox(form, isReadOnly, 'cancellationsDelaysCarrierWeatherClaimAccidentOnWay', messages.cancellationsDelaysCarrierWeatherClaimAccidentOnWay)}
      {this.isChecked(form, 'cancellationsDelaysCarrierWeatherClaimAccidentOnWay') && this.renderCancellationsDelaysCarrierWeatherExpenseOptions(form, isReadOnly, 'travelCostsTransportCancellationsDelaysCarrierWeatherAccidentOnWay', this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_ACCIDENT_ON_WAY_TRANSPORT_RESET_FIELDS, this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_ACCIDENT_ON_WAY_TRANSPORT_EXPENSE_CONFIGS, this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_ACCIDENT_ON_WAY_OTHER_EXPENSE_CONFIGS)}
      {this.renderCancellationsDelaysCarrierWeatherClaimCheckbox(form, isReadOnly, 'cancellationsDelaysCarrierWeatherClaimDelayOfPrivateVehicle', messages.cancellationsDelaysCarrierWeatherClaimDelayOfPrivateVehicle)}
      {this.isChecked(form, 'cancellationsDelaysCarrierWeatherClaimDelayOfPrivateVehicle') && this.renderCancellationsDelaysCarrierWeatherExpenseOptions(form, isReadOnly, 'travelCostsTransportCancellationsDelaysCarrierWeatherDelayOfPrivateVehicle', this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_DELAY_OF_PRIVATE_VEHICLE_TRANSPORT_RESET_FIELDS, this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_DELAY_OF_PRIVATE_VEHICLE_TRANSPORT_EXPENSE_CONFIGS, this.CANCELLATIONS_DELAYS_CARRIER_WEATHER_DELAY_OF_PRIVATE_VEHICLE_OTHER_EXPENSE_CONFIGS)}
    </div>
  );

  renderOtherReasonsClaimCheckbox = (form, isReadOnly, fieldKey, message) => (
    <div key={fieldKey} className="col-sm-12 form-group">
      <input
        disabled={isReadOnly}
        type="checkbox"
        checked={this.isChecked(form, fieldKey)}
        onChange={this.handleOtherReasonsClaimChange(fieldKey)}
      />
      <span>
        <FormattedMessage {...message} />
      </span>
    </div>
  );

  renderOtherReasonsNaturalDisasterExpenseOptions = (form, isReadOnly, transportParentKey, transportResetFields, transportConfigs, otherConfigs) => {
    const travelCostsTransport = this.isChecked(form, transportParentKey);
    return (
      <div className="col-sm-12 form-group ml-4 mt-2">
        <div className="col-sm-12 form-group mb-3">
          <div className="col-sm-12 form-group">
            <input
              disabled={isReadOnly}
              type="checkbox"
              checked={travelCostsTransport}
              onChange={this.handleTravelCostsTransportOtherReasonsNaturalDisasterChange}
            />
            <span>
              <FormattedMessage {...messages.travelCostsTransport} />
            </span>
            <FNOLTooltip
              dataFor={`${transportParentKey}-tooltip`}
              html={true}
              content={this.getTravelCostsTransportTooltipHtml()}
            />
          </div>
          {travelCostsTransport && (
            <div className="col-sm-12 form-group ml-4 mt-2">
              {transportConfigs.map(({ itemsKey, fieldKey, label, tooltip }) => {
                const checked = this.isChecked(form, fieldKey);
                const items = this.getFormValue(form, itemsKey);
                const itemsList = Array.isArray(items) ? items : [];
                return (
                  <div key={itemsKey} className="col-sm-12 form-group mb-3">
                    <div className="col-sm-12 form-group">
                      <input
                        disabled={isReadOnly}
                        type="checkbox"
                        checked={checked}
                        onChange={this.handleTransportSubOptionChangeOtherReasonsNaturalDisaster(fieldKey, itemsKey)}
                      />
                      <span>
                        <FormattedMessage {...label} />
                      </span>
                      {tooltip && (
                        <FNOLTooltip
                          dataFor={`${itemsKey}-tooltip`}
                          content={this.props.intl.formatMessage(tooltip)}
                        />
                      )}
                    </div>
                    {checked && (
                      <div className="col-sm-12 form-group ml-4 mt-2">
                        <HealthExpenseList
                          items={itemsList}
                          itemsKey={itemsKey}
                          onChange={this.handleTransportExpenseItemsChange}
                          disabled={isReadOnly}
                          validatorGroup={this.validatorGroup}
                          validationCounter={this.state.validationCounter}
                          insuredsList={this.props.insuredsList}
                          intl={this.props.intl}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {otherConfigs.map(({ itemsKey, fieldKey, label, tooltip }) => {
          const checked = this.isChecked(form, fieldKey);
          const items = this.getFormValue(form, itemsKey);
          const itemsList = Array.isArray(items) ? items : [];
          return (
            <div key={itemsKey} className="col-sm-12 form-group mb-3">
              <div className="col-sm-12 form-group">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  checked={checked}
                  onChange={this.handleTransportSubOptionChangeOtherReasonsNaturalDisaster(fieldKey, itemsKey)}
                />
                <span>
                  <FormattedMessage {...label} />
                </span>
                {tooltip && (
                  <FNOLTooltip
                    dataFor={`${itemsKey}-tooltip`}
                    content={this.props.intl.formatMessage(tooltip)}
                  />
                )}
              </div>
              {checked && (
                <div className="col-sm-12 form-group ml-4 mt-2">
                  <HealthExpenseList
                    items={itemsList}
                    itemsKey={itemsKey}
                    onChange={this.handleTransportExpenseItemsChange}
                    disabled={isReadOnly}
                    validatorGroup={this.validatorGroup}
                    validationCounter={this.state.validationCounter}
                    insuredsList={this.props.insuredsList}
                    intl={this.props.intl}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  renderOtherReasonsUnforeseeableEventExpenseOptions = (form, isReadOnly, transportParentKey, transportResetFields, transportConfigs, otherConfigs) => {
    const travelCostsTransport = this.isChecked(form, transportParentKey);
    return (
      <div className="col-sm-12 form-group ml-4 mt-2">
        <div className="col-sm-12 form-group mb-3">
          <div className="col-sm-12 form-group">
            <input
              disabled={isReadOnly}
              type="checkbox"
              checked={travelCostsTransport}
              onChange={this.handleTravelCostsTransportOtherReasonsUnforeseeableEventChange}
            />
            <span>
              <FormattedMessage {...messages.travelCostsTransport} />
            </span>
            <FNOLTooltip
              dataFor={`${transportParentKey}-tooltip`}
              html={true}
              content={this.getTravelCostsTransportTooltipHtml()}
            />
          </div>
          {travelCostsTransport && (
            <div className="col-sm-12 form-group ml-4 mt-2">
              {transportConfigs.map(({ itemsKey, fieldKey, label, tooltip }) => {
                const checked = this.isChecked(form, fieldKey);
                const items = this.getFormValue(form, itemsKey);
                const itemsList = Array.isArray(items) ? items : [];
                return (
                  <div key={itemsKey} className="col-sm-12 form-group mb-3">
                    <div className="col-sm-12 form-group">
                      <input
                        disabled={isReadOnly}
                        type="checkbox"
                        checked={checked}
                        onChange={this.handleTransportSubOptionChangeOtherReasonsUnforeseeableEvent(fieldKey, itemsKey)}
                      />
                      <span>
                        <FormattedMessage {...label} />
                      </span>
                      {tooltip && (
                        <FNOLTooltip
                          dataFor={`${itemsKey}-tooltip`}
                          content={this.props.intl.formatMessage(tooltip)}
                        />
                      )}
                    </div>
                    {checked && (
                      <div className="col-sm-12 form-group ml-4 mt-2">
                        <HealthExpenseList
                          items={itemsList}
                          itemsKey={itemsKey}
                          onChange={this.handleTransportExpenseItemsChange}
                          disabled={isReadOnly}
                          validatorGroup={this.validatorGroup}
                          validationCounter={this.state.validationCounter}
                          insuredsList={this.props.insuredsList}
                          intl={this.props.intl}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {otherConfigs.map(({ itemsKey, fieldKey, label, tooltip }) => {
          const checked = this.isChecked(form, fieldKey);
          const items = this.getFormValue(form, itemsKey);
          const itemsList = Array.isArray(items) ? items : [];
          return (
            <div key={itemsKey} className="col-sm-12 form-group mb-3">
              <div className="col-sm-12 form-group">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  checked={checked}
                  onChange={this.handleTransportSubOptionChangeOtherReasonsUnforeseeableEvent(fieldKey, itemsKey)}
                />
                <span>
                  <FormattedMessage {...label} />
                </span>
                {tooltip && (
                  <FNOLTooltip
                    dataFor={`${itemsKey}-tooltip`}
                    content={this.props.intl.formatMessage(tooltip)}
                  />
                )}
              </div>
              {checked && (
                <div className="col-sm-12 form-group ml-4 mt-2">
                  <HealthExpenseList
                    items={itemsList}
                    itemsKey={itemsKey}
                    onChange={this.handleTransportExpenseItemsChange}
                    disabled={isReadOnly}
                    validatorGroup={this.validatorGroup}
                    validationCounter={this.state.validationCounter}
                    insuredsList={this.props.insuredsList}
                    intl={this.props.intl}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  renderOtherReasonsFollowUpCheckboxes = (form, isReadOnly) => (
    <div className="col-sm-12 form-group ml-4 mt-2">
      {this.renderOtherReasonsClaimCheckbox(form, isReadOnly, 'otherReasonsClaimNaturalDisaster', messages.otherReasonsClaimNaturalDisaster)}
      {this.isChecked(form, 'otherReasonsClaimNaturalDisaster') && this.renderOtherReasonsNaturalDisasterExpenseOptions(form, isReadOnly, 'travelCostsTransportOtherReasonsNaturalDisaster', this.OTHER_REASONS_NATURAL_DISASTER_TRANSPORT_RESET_FIELDS, this.OTHER_REASONS_NATURAL_DISASTER_TRANSPORT_EXPENSE_CONFIGS, this.OTHER_REASONS_NATURAL_DISASTER_OTHER_EXPENSE_CONFIGS)}
      {this.renderOtherReasonsClaimCheckbox(form, isReadOnly, 'otherReasonsClaimUnforeseeableEvent', messages.otherReasonsClaimUnforeseeableEvent)}
      {this.isChecked(form, 'otherReasonsClaimUnforeseeableEvent') && this.renderOtherReasonsUnforeseeableEventExpenseOptions(form, isReadOnly, 'travelCostsTransportOtherReasonsUnforeseeableEvent', this.OTHER_REASONS_UNFORESEEABLE_EVENT_TRANSPORT_RESET_FIELDS, this.OTHER_REASONS_UNFORESEEABLE_EVENT_TRANSPORT_EXPENSE_CONFIGS, this.OTHER_REASONS_UNFORESEEABLE_EVENT_OTHER_EXPENSE_CONFIGS)}
    </div>
  );

  renderExpenseTypeItem = (key, expensesSelected, form, isReadOnly) => {
    const msg = this.EXPENSE_TYPE_MESSAGES[key];
    return (
      <div key={key} className="col-sm-12 form-group">
        <input
          disabled={isReadOnly}
          type="checkbox"
          name={key}
          checked={expensesSelected.includes(key)}
          onChange={this.handleExpenseTypesSelected('form.expenseTypesClaiming')}
        />
        <span>
          {msg && <FormattedMessage {...msg} />}
        </span>
        {key === 'health' && expensesSelected.includes('health') && this.renderHealthFollowUpCheckboxes(form, isReadOnly)}
        {key === 'pregnancyAndAdoption' && expensesSelected.includes('pregnancyAndAdoption') && this.renderPregnancyAndAdoptionFollowUpCheckboxes(form, isReadOnly)}
        {key === 'employmentAndEducation' && expensesSelected.includes('employmentAndEducation') && this.renderEmploymentEducationFollowUpCheckboxes(form, isReadOnly)}
        {key === 'legalIssues' && expensesSelected.includes('legalIssues') && this.renderLegalIssuesFollowUpCheckboxes(form, isReadOnly)}
        {key === 'cancellationsDelaysCarrierWeather' && expensesSelected.includes('cancellationsDelaysCarrierWeather') && this.renderCancellationsDelaysCarrierWeatherFollowUpCheckboxes(form, isReadOnly)}
        {key === 'otherReasons' && expensesSelected.includes('otherReasons') && this.renderOtherReasonsFollowUpCheckboxes(form, isReadOnly)}
      </div>
    );
  };

  renderExpenseTypesSection = (expensesSelected, form, isReadOnly) => (
    <div className="col-sm-12 form-group">
      <Validate
        name="expenseTypesClaiming"
        validators={[required]}
        group={this.validatorGroup}
        addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
        validChangeInGroup={this.validatorGroup.validChangeInGroup}
        validationCounter={this.state.validationCounter}
        validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
        defaultValue={expensesSelected}
        fieldName={messages.whatTypeOfExpensesQuestion}
        index={0}
      >
        <input
          type="hidden"
          value={Array.isArray(expensesSelected) ? expensesSelected.join(',') : ''}
          readOnly
          onChange={() => { }}
        />
        {this.EXPENSE_TYPE_KEYS.map((key) => this.renderExpenseTypeItem(key, expensesSelected, form, isReadOnly))}
        <ErrorMessage for="required">
          <FormattedMessage
            {...messages.isRequired}
            values={{ defaultMessage: this.props.intl.formatMessage(messages.whatTypeOfExpensesQuestion) }}
          />
        </ErrorMessage>
      </Validate>
    </div>
  );

  render() {
    const { isSummary, submitted, loading, intl, form } = this.props;
    const isReadOnly = submitted || isSummary;
    const expensesSelected = this.getExpensesSelected(form);

    return (
      <div className="app-content">
        {this.renderPageDescription()}
        {this.renderExpenseTypesSection(expensesSelected, form, isReadOnly)}
        {!isSummary && (
          <WizardNavigationButtons
            onBack={this.props.prev}
            onNext={this.handleNext}
            onSave={this.handleSaveButtonClick}
            canMoveNext
            listLength={1}
            submitted={submitted}
            loading={loading}
            saveButtonId={this.saveButtonId}
            showSpinnerForElement={this.showSpinnerForElement}
            handleKeyDown={this.handleKeyDown}
            intl={intl}
            hasFormChanges={this.props.hasFormChanges}
            nextButtonRef={(ref) => { this.nextButton = ref; }}
          />
        )}
        <ReactResizeDetector handleHeight onResize={this.onResize} />
      </div>
    );
  }
}

TripInterruptionTugo.propTypes = {
  form: PropTypes.object,
  onFormDataChange: PropTypes.func,
  prev: PropTypes.func,
  next: PropTypes.func,
  onFormDataSave: PropTypes.func,
  loading: PropTypes.bool,
  isSummary: PropTypes.bool,
  submitted: PropTypes.bool,
  adjust: PropTypes.func,
  makeValid: PropTypes.func,
  makeError: PropTypes.func,
  onNext: PropTypes.func,
  step: PropTypes.number,
  currentStep: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isReadOnly: PropTypes.bool,
  errors: PropTypes.any,
  onResetFormCustom: PropTypes.func,
  validateStep: PropTypes.string,
  stepName: PropTypes.string,
  intl: intlShape.isRequired,
  insuredsList: PropTypes.any,
  isClaimForFieldEnabled: PropTypes.bool,
  hasFormChanges: PropTypes.bool,
  setFormHasChanges: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  form: makeSelectTripInterruptionTugo(),
  errors: makeSelectTripInterruptionTugoErrors(),
  currentStep: makeSelectCurrentStep(),
  loading: makeSelectProgressBarStatus(),
  insuredsList: makeSelectInsuredsList(),
  isClaimForFieldEnabled: makeSelectIsClaimForFieldEnabled(),
  hasFormChanges: makeSelectFormHasChanges(),
});

const mapDispatchToProps = (dispatch) => ({
  onFormDataChange: (propertyName, value) => dispatch(handleFormDataChange(propertyName, value)),
  onFormDataSave: () => dispatch(saveFormData()),
  onNext: (errors) => dispatch(nextSlide(errors)),
  onResetFormCustom: (fields) => dispatch(resetFormFields(fields)),
  setFormHasChanges: (hasFormChanges) => dispatch(setFormHasChanges(hasFormChanges)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withReducer = injectReducer({ key: 'tripInterruptionTugo', reducer });

export default compose(
  withReducer,
  withConnect,
  injectIntl,
)(TripInterruptionTugo);



// WEBPACK FOOTER //
// ./app/containers/FastCover/Tugo.TripInterruption/index.js